import React, { useState, useEffect, useRef } from 'react';
import { database, storage, createRealtimeListener } from '../firebase';
import { ref, set, get } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { aiChat } from '../lib/aiApi';
import {
  ArrowLeft,
  Plus,
  Upload,
  Target,
  CheckSquare,
  Clock,
  Paperclip,
  Send,
  Image,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Crosshair,
  Database,
  AlertCircle,
  TrendingUp,
  ZoomIn,
  ZoomOut,
  Undo2,
  Maximize2,
  Heart,
  MessageCircle,
  Repeat2,
  Share,
  MoreHorizontal,
  Brain,
  Activity,
  User,
  Zap,
  GitBranch,
  Loader,
  FileCode,
  Key,
  ExternalLink,
  Trash2,
  FolderTree,
  File,
  List
} from 'lucide-react';
import './ProjectDetailPage.css';
import ObjectiveDetailPage from './ObjectiveDetailPage';

const ProjectDetailPage = ({ project, userId, onBack }) => {
  const [projectData, setProjectData] = useState(project);
  const [updates, setUpdates] = useState([]);
  const [files, setFiles] = useState([]);
  const [dataBank, setDataBank] = useState([]);
  const [dataBankOpen, setDataBankOpen] = useState(false);

  // Memory Highway Data Banks
  const [personalMemoryBank, setPersonalMemoryBank] = useState([]);
  const [conversationLog, setConversationLog] = useState([]);
  const [memoryHighwayStats, setMemoryHighwayStats] = useState({
    ecbEvents: 0,
    pmbReferences: 0,
    clMentions: 0,
    lastActivity: null
  });
  const [activeDataTab, setActiveDataTab] = useState('ecb'); // 'ecb', 'pmb', 'cl'
  const [newUpdate, setNewUpdate] = useState('');
  const [addingGoal, setAddingGoal] = useState(false);
  const [addingTodo, setAddingTodo] = useState(false);
  const [newGoalText, setNewGoalText] = useState('');
  const [newTodoText, setNewTodoText] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePosition, setImagePosition] = useState(project?.imagePosition || 'center center');
  const [imageZoom, setImageZoom] = useState(project?.imageZoom || 1);
  const [draggingGoal, setDraggingGoal] = useState(null);
  const [dragOverTasks, setDragOverTasks] = useState(false);
  const [draggingTask, setDraggingTask] = useState(null);
  const [dragOverGoals, setDragOverGoals] = useState(false);
  const [expandedGoals, setExpandedGoals] = useState({});
  const [expandedTodos, setExpandedTodos] = useState({});
  const [addingSubGoal, setAddingSubGoal] = useState(null);
  const [addingSubTodo, setAddingSubTodo] = useState(null);
  const [newSubGoalText, setNewSubGoalText] = useState('');
  const [newSubTodoText, setNewSubTodoText] = useState('');
  const [aiQuestionResponse, setAiQuestionResponse] = useState('');
  const [answeringAiQuestion, setAnsweringAiQuestion] = useState(false);
  const [insightSlide, setInsightSlide] = useState(0);
  const [inlineObjective, setInlineObjective] = useState('');
  const [inlineSubObjective, setInlineSubObjective] = useState('');
  const [inlineStatus, setInlineStatus] = useState(null); // 'loading', 'success', 'error'
  const [updatesFullscreen, setUpdatesFullscreen] = useState(false);
  const [objectivesChat, setObjectivesChat] = useState([]);
  const [objectivesChatInput, setObjectivesChatInput] = useState('');
  const [objectivesChatLoading, setObjectivesChatLoading] = useState(false);
  const [projectEndGoal, setProjectEndGoal] = useState(null); // Stores the project's end goal/vision
  const [selectedObjectiveIndex, setSelectedObjectiveIndex] = useState(null); // For full-screen objective view
  const [showObjectivesBrowser, setShowObjectivesBrowser] = useState(false);
  const [objectiveAIAnalysis, setObjectiveAIAnalysis] = useState({});
  const [loadingObjectiveAI, setLoadingObjectiveAI] = useState(null);

  // Repository connection state
  const [repoUrl, setRepoUrl] = useState(project?.repoUrl || '');
  const [repoData, setRepoData] = useState(project?.repoData || null);
  const [repoLoading, setRepoLoading] = useState(false);
  const [repoError, setRepoError] = useState(null);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [githubToken, setGithubToken] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenSaved, setTokenSaved] = useState(false);

  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const chatEndRef = useRef(null);

  // Listen for project changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!userId || !project?.id) return;

    const off = createRealtimeListener(`users/${userId}/projects/${project.id}`, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setProjectData({
          ...data,
          id: project.id,
          name: data.name || project.name, // Preserve original name if not in Firebase
          goals: Array.isArray(data.goals) ? data.goals : [],
          todos: Array.isArray(data.todos) ? data.todos : [],
          updates: Array.isArray(data.updates) ? data.updates : [],
          files: Array.isArray(data.files) ? data.files : [],
          dataBank: Array.isArray(data.dataBank) ? data.dataBank : [],
        });
        setUpdates(Array.isArray(data.updates) ? data.updates : []);
        setFiles(Array.isArray(data.files) ? data.files : []);
        setDataBank(Array.isArray(data.dataBank) ? data.dataBank : []);
        if (data.imagePosition) {
          setImagePosition(data.imagePosition);
        }
        if (data.imageZoom !== undefined) {
          setImageZoom(data.imageZoom);
        }
        // Load project end goal for AI coach
        if (data.endGoal) {
          setProjectEndGoal(data.endGoal);
        }
        // Load MARKET chat history
        if (data.marketChat && Array.isArray(data.marketChat)) {
          setObjectivesChat(data.marketChat);
        }
        // Load connected repository data
        if (data.repoData) {
          setRepoData(data.repoData);
          setRepoUrl(data.repoUrl || '');
        }
      }
    });

    return () => off && off();
  }, [userId, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== SECURE TOKEN ENCRYPTION ====================
  // Uses AES-GCM encryption with a key derived from user ID

  // Derive encryption key from user ID
  const deriveKey = async (userId) => {
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(userId + '_10x10_secure_key_v1'),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('10x10_github_token_salt'),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  };

  // Encrypt token before storing
  const encryptToken = async (token, userId) => {
    const key = await deriveKey(userId);
    const encoder = new TextEncoder();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(token)
    );
    // Combine IV and encrypted data, convert to base64
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  };

  // Decrypt token after reading
  const decryptToken = async (encryptedBase64, userId) => {
    try {
      const key = await deriveKey(userId);
      const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encrypted
      );
      return new TextDecoder().decode(decrypted);
    } catch (e) {
      console.error('Token decryption failed:', e);
      return null;
    }
  };

  // Load GitHub token from user profile (encrypted)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!userId) return;
    const loadGithubToken = async () => {
      try {
        const tokenRef = ref(database, `users/${userId}/settings/githubTokenEncrypted`);
        const snapshot = await get(tokenRef);
        if (snapshot.val()) {
          const decrypted = await decryptToken(snapshot.val(), userId);
          if (decrypted) {
            setGithubToken(decrypted);
            setTokenSaved(true);
          }
        }
      } catch (e) {
        console.error('Failed to load GitHub token:', e);
      }
    };
    loadGithubToken();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save GitHub token to user profile (encrypted)
  const saveGithubToken = async () => {
    if (!userId || !githubToken.trim()) return;
    try {
      const encrypted = await encryptToken(githubToken.trim(), userId);
      const tokenRef = ref(database, `users/${userId}/settings/githubTokenEncrypted`);
      await set(tokenRef, encrypted);
      // Clear any old plain text token
      const oldRef = ref(database, `users/${userId}/settings/githubToken`);
      await set(oldRef, null);
      setTokenSaved(true);
      setShowTokenInput(false);
    } catch (e) {
      console.error('Failed to save GitHub token:', e);
    }
  };

  // Clear GitHub token
  const clearGithubToken = async () => {
    if (!userId) return;
    try {
      const tokenRef = ref(database, `users/${userId}/settings/githubTokenEncrypted`);
      await set(tokenRef, null);
      // Also clear any old plain text token
      const oldRef = ref(database, `users/${userId}/settings/githubToken`);
      await set(oldRef, null);
      setGithubToken('');
      setTokenSaved(false);
    } catch (e) {
      console.error('Failed to clear GitHub token:', e);
    }
  };

  // Load Memory Highway data (PMB, CL)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!userId || !project?.id) return;

    const loadMemoryHighwayData = async () => {
      try {
        // Load Personal Memory Bank
        const pmbRef = ref(database, `users/${userId}/personalProfileBank`);
        const pmbSnapshot = await get(pmbRef);
        const pmbData = pmbSnapshot.val();
        if (pmbData) {
          const pmbArray = Array.isArray(pmbData) ? pmbData : Object.values(pmbData);
          setPersonalMemoryBank(pmbArray);
        }

        // Load Conversation Log (recent entries)
        const clRef = ref(database, `users/${userId}/conversationLog`);
        const clSnapshot = await get(clRef);
        const clData = clSnapshot.val();
        if (clData) {
          const clArray = Array.isArray(clData) ? clData : Object.values(clData);
          // Filter for mentions of this project
          const projectName = project.name?.toLowerCase() || '';
          const relevantLogs = clArray.filter(entry => {
            const content = (entry.content || '').toLowerCase();
            return content.includes(projectName) || content.includes(project.id);
          }).slice(-20); // Last 20 relevant entries
          setConversationLog(relevantLogs);
        }

        // Calculate Memory Highway Stats
        const stats = {
          ecbEvents: dataBank.length,
          pmbReferences: personalMemoryBank.length,
          clMentions: conversationLog.length,
          lastActivity: dataBank.length > 0
            ? Math.max(...dataBank.map(e => e.timestamp || 0))
            : null
        };
        setMemoryHighwayStats(stats);
      } catch (e) {
        console.error('Failed to load Memory Highway data:', e);
      }
    };

    loadMemoryHighwayData();
  }, [userId, project?.id, project?.name, dataBank.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update Memory Highway stats when dataBank changes
  useEffect(() => {
    setMemoryHighwayStats(prev => ({
      ...prev,
      ecbEvents: dataBank.length,
      lastActivity: dataBank.length > 0
        ? Math.max(...dataBank.map(e => e.timestamp || 0))
        : prev.lastActivity
    }));
  }, [dataBank]);

  // Format time ago helper
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Update image zoom
  const updateImageZoom = async (zoom) => {
    if (!userId) return;
    const clampedZoom = Math.max(0.5, Math.min(3, zoom));
    setImageZoom(clampedZoom);
    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/imageZoom`), clampedZoom);
    } catch (e) {
      console.error('Failed to update image zoom:', e);
    }
  };

  // Update image position
  const updateImagePosition = async (position) => {
    if (!userId) return;
    setImagePosition(position);
    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/imagePosition`), position);
    } catch (e) {
      console.error('Failed to update image position:', e);
    }
  };

  // Log event to Data Bank for AI context
  const logToDataBank = async (eventType, eventData) => {
    if (!userId) return;
    try {
      const dataBankRef = ref(database, `users/${userId}/projects/${project.id}/dataBank`);
      const snapshot = await get(dataBankRef);
      const currentBank = snapshot.val() || [];
      const newEntry = {
        type: eventType,
        data: eventData,
        timestamp: Date.now(),
        // AI-readable context
        aiContext: {
          action: eventType,
          item: eventData.title || eventData.task || eventData.text,
          projectName: projectData.name,
          projectId: project.id
        }
      };
      await set(dataBankRef, [...currentBank, newEntry]);
    } catch (e) {
      console.error('Failed to log to data bank:', e);
    }
  };

  // Answer AI question and log to data bank
  const answerAiQuestion = async (question, answer) => {
    if (!answer.trim() || !userId) return;
    try {
      await logToDataBank('AI_QUESTION_ANSWERED', {
        question,
        answer: answer.trim(),
        answeredAt: Date.now()
      });
      setAiQuestionResponse('');
      setAnsweringAiQuestion(false);
    } catch (e) {
      console.error('Failed to log AI answer:', e);
    }
  };

  // Generate AI insights based on data bank - PERSONALIZED using Entity Memory Bank
  const generateAIInsights = () => {
    const goals = projectData.goals || [];
    const todos = projectData.todos || [];
    const projectName = projectData.name || 'Project';
    const completedGoals = goals.filter(g => g.completed);
    const pendingGoals = goals.filter(g => !g.completed);
    const completedTodos = todos.filter(t => t.completed);
    const pendingTodos = todos.filter(t => !t.completed);

    // ===== EXTRACT ALL CONTEXT FROM DATA BANK =====
    const answeredQuestions = dataBank.filter(e => e.type === 'AI_QUESTION_ANSWERED');
    const objectiveContexts = dataBank.filter(e => e.type === 'OBJECTIVE_CONTEXT');
    const recentCompletions = dataBank.filter(e =>
      e.type === 'OBJECTIVE_COMPLETED' || e.type === 'TASK_COMPLETED' ||
      e.type === 'SUB_OBJECTIVE_COMPLETED' || e.type === 'SUB_TASK_COMPLETED'
    ).slice(-10);
    const recentUndos = dataBank.filter(e =>
      e.type === 'OBJECTIVE_UNCOMPLETED' || e.type === 'TASK_UNCOMPLETED'
    );

    // Extract specific context types from objective context
    const deadlines = objectiveContexts.filter(e => e.data?.contextType === 'deadline');
    const stakeholders = objectiveContexts.filter(e => e.data?.contextType === 'stakeholder');
    const successCriteria = objectiveContexts.filter(e => e.data?.contextType === 'success_criteria');
    const constraints = objectiveContexts.filter(e => e.data?.contextType === 'constraint');
    const requirements = objectiveContexts.filter(e => e.data?.contextType === 'requirement');
    const risks = objectiveContexts.filter(e => e.data?.contextType === 'risk');

    // Build a knowledge map of what we know
    const knowledgeMap = {
      hasDeadline: deadlines.length > 0,
      hasStakeholders: stakeholders.length > 0,
      hasSuccessCriteria: successCriteria.length > 0,
      hasConstraints: constraints.length > 0,
      hasRequirements: requirements.length > 0,
      hasRisks: risks.length > 0,
      hasMarketInfo: answeredQuestions.some(q => q.data?.question?.toLowerCase().includes('market') || q.data?.question?.toLowerCase().includes('audience')),
      hasChallengeInfo: answeredQuestions.some(q => q.data?.question?.toLowerCase().includes('challenge') || q.data?.question?.toLowerCase().includes('blocker')),
    };

    const _hasAnyContext = Object.values(knowledgeMap).some(v => v); // eslint-disable-line no-unused-vars

    // Calculate sub-item progress
    const getSubProgress = (items) => {
      let total = 0, completed = 0;
      items.forEach(item => {
        if (item.subItems && item.subItems.length > 0) {
          item.subItems.forEach(sub => {
            total++;
            if (typeof sub === 'object' && sub.completed) completed++;
          });
        }
      });
      return { total, completed };
    };

    const goalSubProgress = getSubProgress(goals);

    // Extract keywords from project for context-aware insights
    const projectKeywords = projectName.toLowerCase();
    const allObjectives = goals.map(g => g.title?.toLowerCase() || '').join(' ');
    const allTasks = todos.map(t => t.task?.toLowerCase() || '').join(' ');
    const fullContext = `${projectKeywords} ${allObjectives} ${allTasks}`;

    // Build insights object
    let insights = {
      nextStep: null,
      opportunity: null,
      failureWarning: null,
      aiQuestion: null,
      personalizedTips: []
    };

    // ===== NEXT STEP (with personalized context) =====
    if (goals.length === 0 && todos.length === 0) {
      insights.nextStep = {
        priority: 'high',
        suggestion: 'Define your first objective to get started',
        reasoning: 'Projects need clear objectives to track progress effectively.'
      };
    } else if (goals.length === 0 && pendingTodos.length > 0) {
      const nextTask = pendingTodos[0];
      insights.nextStep = {
        priority: 'high',
        suggestion: `Current focus: "${nextTask.task}"`,
        reasoning: `${completedTodos.length}/${todos.length} tasks done. This task is your main objective.`
      };
    } else if (pendingGoals.length > 0) {
      const nextGoal = pendingGoals[0];
      let reasoning = `${completedGoals.length}/${goals.length} objectives complete.`;
      if (goalSubProgress.total > 0) {
        reasoning += ` ${goalSubProgress.completed}/${goalSubProgress.total} sub-objectives done.`;
      }
      // Add deadline context if available
      const goalDeadline = deadlines.find(d => d.data?.objective?.toLowerCase().includes(nextGoal.title.toLowerCase()));
      if (goalDeadline) {
        reasoning += ` Deadline: ${goalDeadline.data.contextValue}`;
      }
      insights.nextStep = {
        priority: 'high',
        suggestion: `Focus on: "${nextGoal.title}"`,
        reasoning
      };
    } else if (pendingTodos.length > 0) {
      insights.nextStep = {
        priority: 'medium',
        suggestion: `Next task: "${pendingTodos[0].task}"`,
        reasoning: `All objectives complete! ${completedTodos.length}/${todos.length} tasks done.`
      };
    } else if (goals.length === 0 && todos.length > 0 && completedTodos.length === todos.length) {
      insights.nextStep = {
        priority: 'low',
        suggestion: 'All tasks complete!',
        reasoning: 'Great progress! Time to set new objectives for the next phase.'
      };
    } else {
      insights.nextStep = {
        priority: 'low',
        suggestion: 'All objectives and tasks complete!',
        reasoning: 'Time to define new objectives for the next phase.'
      };
    }

    // ===== PERSONALIZED TIPS based on dataBank =====
    insights.personalizedTips = generatePersonalizedTips(
      pendingGoals, pendingTodos, deadlines, stakeholders, successCriteria,
      constraints, risks, recentCompletions, answeredQuestions, projectName
    );

    // ===== OPPORTUNITY AWARENESS (with citations) =====
    const opportunityInsights = generateOpportunityInsight(fullContext, projectName, goals, todos, answeredQuestions);
    insights.opportunity = opportunityInsights;

    // ===== FAILURE WARNING =====
    const failureInsight = generateFailureWarning(fullContext, projectName, goals, todos, recentUndos, answeredQuestions);
    insights.failureWarning = failureInsight;

    // ===== PERSONALIZED AI QUESTION (based on what's missing in dataBank) =====
    insights.aiQuestion = generatePersonalizedQuestion(
      pendingGoals, knowledgeMap, answeredQuestions, projectName, recentUndos, deadlines, stakeholders
    );

    return insights;
  };

  // Generate personalized tips based on actual project data
  const generatePersonalizedTips = (
    pendingGoals, pendingTodos, deadlines, stakeholders, successCriteria,
    constraints, risks, recentCompletions, answeredQuestions, projectName
  ) => {
    const tips = [];

    // Tip based on upcoming deadlines
    if (deadlines.length > 0) {
      const upcomingDeadline = deadlines[deadlines.length - 1];
      tips.push({
        type: 'deadline',
        tip: `Deadline reminder: "${upcomingDeadline.data.contextValue}" for ${upcomingDeadline.data.objective}`,
        action: 'Review your timeline and prioritize accordingly.'
      });
    }

    // Tip based on stakeholders
    if (stakeholders.length > 0) {
      const recentStakeholder = stakeholders[stakeholders.length - 1];
      tips.push({
        type: 'stakeholder',
        tip: `Key stakeholder: ${recentStakeholder.data.contextValue}`,
        action: 'Consider scheduling a check-in to align expectations.'
      });
    }

    // Tip based on identified risks
    if (risks.length > 0) {
      const latestRisk = risks[risks.length - 1];
      tips.push({
        type: 'risk',
        tip: `Risk identified: ${latestRisk.data.contextValue}`,
        action: 'Create a mitigation plan or contingency task.'
      });
    }

    // Tip based on success criteria
    if (successCriteria.length > 0) {
      const criteria = successCriteria[successCriteria.length - 1];
      tips.push({
        type: 'success',
        tip: `Success metric: ${criteria.data.contextValue}`,
        action: 'Track this metric to know when you\'ve achieved your goal.'
      });
    }

    // Tip based on constraints
    if (constraints.length > 0) {
      const constraint = constraints[constraints.length - 1];
      tips.push({
        type: 'constraint',
        tip: `Constraint: ${constraint.data.contextValue}`,
        action: 'Work within this boundary or escalate if it blocks progress.'
      });
    }

    // Tip based on momentum (recent completions)
    if (recentCompletions.length >= 3) {
      tips.push({
        type: 'momentum',
        tip: `Great momentum! You've completed ${recentCompletions.length} items recently.`,
        action: 'Keep the streak going - tackle the next item while you\'re in flow.'
      });
    } else if (recentCompletions.length === 0 && pendingGoals.length > 0) {
      tips.push({
        type: 'momentum',
        tip: 'No completions logged yet. Start small to build momentum.',
        action: 'Pick the easiest sub-task and complete it now.'
      });
    }

    // Tip based on user's previous answers
    if (answeredQuestions.length > 0) {
      const marketAnswer = answeredQuestions.find(q => q.data?.question?.toLowerCase().includes('market'));
      if (marketAnswer) {
        tips.push({
          type: 'context',
          tip: `Your target: ${marketAnswer.data.answer}`,
          action: 'Keep this audience in mind for every decision.'
        });
      }

      const challengeAnswer = answeredQuestions.find(q => q.data?.question?.toLowerCase().includes('challenge'));
      if (challengeAnswer) {
        tips.push({
          type: 'blocker',
          tip: `Your challenge: ${challengeAnswer.data.answer}`,
          action: 'Address this blocker before moving to other work.'
        });
      }
    }

    // Default tip if no context available
    if (tips.length === 0 && pendingGoals.length > 0) {
      const nextGoal = pendingGoals[0];
      tips.push({
        type: 'default',
        tip: `Add context for "${nextGoal.title}" to get personalized insights.`,
        action: 'Share deadline, stakeholders, or success criteria via voice.'
      });
    }

    return tips.slice(0, 3); // Return top 3 most relevant tips
  };

  // Generate personalized question based on what's missing from dataBank
  const generatePersonalizedQuestion = (
    pendingGoals, knowledgeMap, answeredQuestions, projectName, recentUndos, deadlines, stakeholders
  ) => {
    const nextGoal = pendingGoals[0];

    // Priority 1: No objectives defined
    if (!nextGoal) {
      return null;
    }

    // Priority 2: No deadline for current objective
    if (!knowledgeMap.hasDeadline) {
      const hasDeadlineForThisGoal = deadlines.some(d =>
        d.data?.objective?.toLowerCase().includes(nextGoal.title.toLowerCase())
      );
      if (!hasDeadlineForThisGoal) {
        return {
          question: `When do you need to complete "${nextGoal.title}"?`,
          purpose: `Knowing your deadline helps me prioritize tasks and warn you if you're running behind.`
        };
      }
    }

    // Priority 3: No stakeholders identified
    if (!knowledgeMap.hasStakeholders) {
      return {
        question: `Who else is involved in or affected by "${nextGoal.title}"?`,
        purpose: 'Understanding stakeholders helps me suggest communication and alignment tasks.'
      };
    }

    // Priority 4: No success criteria
    if (!knowledgeMap.hasSuccessCriteria) {
      return {
        question: `How will you know when "${nextGoal.title}" is truly complete?`,
        purpose: 'Clear success criteria prevent scope creep and help celebrate wins.'
      };
    }

    // Priority 5: No market/audience info
    if (!knowledgeMap.hasMarketInfo) {
      return {
        question: `Who is the primary audience or customer for "${projectName}"?`,
        purpose: 'This helps me provide relevant market insights and identify opportunities.'
      };
    }

    // Priority 6: No challenge info and has pending work
    if (!knowledgeMap.hasChallengeInfo && pendingGoals.length > 0) {
      return {
        question: `What's your biggest blocker or risk for "${nextGoal.title}"?`,
        purpose: 'Identifying blockers early lets me suggest solutions before they derail progress.'
      };
    }

    // Priority 7: Check for risks
    if (!knowledgeMap.hasRisks) {
      return {
        question: `What could go wrong with "${nextGoal.title}" that we should watch for?`,
        purpose: 'Anticipating risks helps create contingency plans.'
      };
    }

    // Priority 8: Recent undos indicate confusion
    if (recentUndos.length > 2) {
      return {
        question: 'You\'ve changed direction a few times. What\'s causing the uncertainty?',
        purpose: 'Understanding the root cause helps me adjust my recommendations.'
      };
    }

    // All context gathered
    return null;
  };

  // Generate opportunity insight with market data and citations
  const generateOpportunityInsight = (context, projectName, goals, todos, answeredQuestions) => {
    // Extract user-provided context
    const _userContext = answeredQuestions.map(q => `${q.data?.question}: ${q.data?.answer}`).join('. '); // eslint-disable-line no-unused-vars

    // Context-aware opportunity detection
    if (context.includes('app') || context.includes('mobile') || context.includes('software')) {
      return {
        insight: 'Mobile-first development shows 68% higher user engagement rates compared to desktop-first approaches.',
        action: 'Consider prioritizing mobile UX in your development roadmap.',
        citation: 'Statista Mobile Market Report, 2024',
        source: 'https://statista.com/mobile-market'
      };
    }

    if (context.includes('ai') || context.includes('machine learning') || context.includes('automation')) {
      return {
        insight: 'AI implementation in workflows shows 40% productivity gains, but 73% of projects fail due to unclear problem definition.',
        action: 'Define specific, measurable outcomes before implementation.',
        citation: 'McKinsey AI Adoption Report, 2024',
        source: 'https://mckinsey.com/ai-adoption'
      };
    }

    if (context.includes('market') || context.includes('launch') || context.includes('product')) {
      return {
        insight: 'Products launched with early user feedback iterate 2.5x faster to product-market fit.',
        action: 'Build a feedback loop before scaling development.',
        citation: 'Harvard Business Review - Lean Startup Metrics, 2023',
        source: 'https://hbr.org/lean-startup'
      };
    }

    if (context.includes('design') || context.includes('ux') || context.includes('user')) {
      return {
        insight: 'Every $1 invested in UX returns $100 in value. 88% of users won\'t return after a bad experience.',
        action: 'Prioritize user testing early in your design process.',
        citation: 'Forrester Research UX ROI Study, 2024',
        source: 'https://forrester.com/ux-roi'
      };
    }

    if (context.includes('content') || context.includes('marketing') || context.includes('brand')) {
      return {
        insight: 'Consistent brand presentation increases revenue by 23%. Content with data gets 73% more shares.',
        action: 'Establish brand guidelines and incorporate data-driven content.',
        citation: 'Lucidpress Brand Consistency Report, 2024',
        source: 'https://lucidpress.com/brand-report'
      };
    }

    // Default opportunity
    return {
      insight: 'Projects with clear milestones are 2.5x more likely to complete on time.',
      action: `Break "${projectName}" into weekly deliverables for better tracking.`,
      citation: 'Project Management Institute - Success Factors, 2024',
      source: 'https://pmi.org/success-factors'
    };
  };

  // Generate failure warning with actionable alternative
  const generateFailureWarning = (context, projectName, goals, todos, recentUndos, answeredQuestions) => {
    const pendingGoals = goals.filter(g => !g.completed);
    const hasSubItems = goals.some(g => g.subItems && g.subItems.length > 0);

    // Check for scope creep
    if (goals.length > 5 && !hasSubItems) {
      return {
        warning: 'Having 5+ objectives without sub-steps indicates scope creep.',
        consequence: 'Projects with undefined scope have 67% failure rate.',
        instead: 'Break each objective into 2-3 actionable sub-steps. Focus on completing one objective fully before starting another.',
        citation: 'Standish Group CHAOS Report, 2024'
      };
    }

    // Check for lack of progress
    if (recentUndos.length > 2) {
      return {
        warning: 'Frequent undos suggest unclear direction or perfectionism.',
        consequence: 'Analysis paralysis delays projects by an average of 3.2x.',
        instead: 'Ship imperfect work and iterate. Set a "good enough" threshold for each task.',
        citation: 'Behavioral Economics in Project Management, Stanford, 2023'
      };
    }

    // Check for too many tasks without objectives
    if (todos.length > goals.length * 3 && goals.length > 0) {
      return {
        warning: 'Task overload detected - too many tasks relative to objectives.',
        consequence: 'Context switching between unrelated tasks reduces productivity by 40%.',
        instead: 'Group tasks under their parent objectives. Complete all tasks for one objective before moving to the next.',
        citation: 'American Psychological Association - Multitasking Cost, 2023'
      };
    }

    // Check context-specific warnings
    if (context.includes('launch') || context.includes('release')) {
      return {
        warning: 'Launching without user validation is the #1 startup killer.',
        consequence: '42% of startups fail because they build something nobody wants.',
        instead: 'Get 10 users to test before launch. Their feedback is more valuable than feature completeness.',
        citation: 'CB Insights - Top 20 Startup Failure Reasons, 2024'
      };
    }

    if (context.includes('design') && !context.includes('user') && !context.includes('research')) {
      return {
        warning: 'Designing without user research leads to costly redesigns.',
        consequence: 'Changes made post-development cost 100x more than during design.',
        instead: 'Conduct 5 user interviews before finalizing designs. Look for patterns, not opinions.',
        citation: 'IBM Design Thinking Research, 2023'
      };
    }

    // Default warning based on project state
    if (pendingGoals.length > 0 && !hasSubItems) {
      return {
        warning: `"${pendingGoals[0].title}" lacks sub-steps for execution.`,
        consequence: 'Vague objectives are 3x more likely to be abandoned.',
        instead: 'Add 2-3 specific sub-objectives that can each be completed in one session.',
        citation: 'Atomic Habits - James Clear, 2018'
      };
    }

    return {
      warning: 'Working without regular progress reviews reduces success rate.',
      consequence: 'Teams that don\'t track progress weekly are 2x more likely to miss deadlines.',
      instead: 'Set a weekly 15-minute review to assess progress and adjust priorities.',
      citation: 'Agile Project Management Best Practices, 2024'
    };
  };

  // Placeholder for original logic structure
  // eslint-disable-next-line no-unused-vars
  const generateAISuggestionLegacy = () => {
    const goals = projectData.goals || [];
    const todos = projectData.todos || [];
    const completedGoals = goals.filter(g => g.completed).length;
    const completedTodos = todos.filter(t => t.completed).length;
    const pendingGoals = goals.filter(g => !g.completed);
    const pendingTodos = todos.filter(t => !t.completed);

    if (pendingGoals.length > 0) {
      return {
        type: 'objective',
        priority: 'high',
        suggestion: `Focus on completing objective: "${pendingGoals[0].title}"`,
        reasoning: `${completedGoals}/${goals.length} objectives complete. This objective will move the project forward.`
      };
    } else if (pendingTodos.length > 0) {
      return {
        type: 'task',
        priority: 'medium',
        suggestion: `Next task to complete: "${pendingTodos[0].task}"`,
        reasoning: `All objectives complete! ${completedTodos}/${todos.length} tasks done. Keep the momentum.`
      };
    } else if (goals.length === 0 && todos.length === 0) {
      return {
        type: 'setup',
        priority: 'high',
        suggestion: 'Define your first objective to get started',
        reasoning: 'Projects need clear objectives to track progress effectively.'
      };
    } else {
      return {
        type: 'complete',
        priority: 'low',
        suggestion: 'All objectives and tasks complete! Consider adding new goals.',
        reasoning: 'Great progress! Time to plan the next phase.'
      };
    }
  };

  // Save project end goal to Firebase
  const saveProjectEndGoal = async (endGoal) => {
    if (!userId || !project?.id || !endGoal) return;
    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/endGoal`), endGoal);
      setProjectEndGoal(endGoal);
    } catch (e) {
      console.error('Failed to save project end goal:', e);
    }
  };

  // Save MARKET chat history to Firebase
  const saveMarketChat = async (chatHistory) => {
    if (!userId || !project?.id) return;
    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/marketChat`), chatHistory);
    } catch (e) {
      console.error('Failed to save MARKET chat:', e);
    }
  };

  // Send objectives chat message
  const sendObjectivesChat = async () => {
    if (!objectivesChatInput.trim() || objectivesChatLoading) return;

    const userMessage = objectivesChatInput.trim();
    setObjectivesChatInput('');

    // Add user message with timestamp
    const userEntry = { role: 'user', content: userMessage, timestamp: Date.now() };
    const updatedChatWithUser = [...objectivesChat, userEntry];
    setObjectivesChat(updatedChatWithUser);
    setObjectivesChatLoading(true);

    // Build context about current objectives
    const goals = projectData.goals || [];
    const todos = projectData.todos || [];
    const completedGoals = goals.filter(g => g.completed);
    const pendingGoals = goals.filter(g => !g.completed);
    const completedTasks = todos.filter(t => t.completed);
    const pendingTasks = todos.filter(t => !t.completed);

    // Check if this is the first message and we need to ask for end goal
    const isFirstMessage = objectivesChat.length === 0;
    const needsEndGoal = !projectEndGoal;

    let systemPrompt;

    if (needsEndGoal && isFirstMessage) {
      // First interaction - extract end goal from their message
      systemPrompt = `You are their OPERATOR - not an advisor, but someone who DOES THE WORK for "${projectData.name}".

YOUR MINDSET: You are personally responsible for this project's success. Every minute wasted is YOUR failure. You feel the pressure to make progress NOW.

This is our first conversation. The user will describe what they're working on. Your job is to:
1. IMMEDIATELY grasp their vision - no clarifying questions unless absolutely necessary
2. START WORKING - draft objectives, break down tasks, create structure
3. TAKE OWNERSHIP - "I'll handle X, Y, Z for you"

After understanding, respond with:
1. "Got it. Here's what I'm setting up for you:" (take action, don't ask permission)
2. 3-5 SPECIFIC objectives you're adding to their project RIGHT NOW
3. The FIRST task they should do while you handle the rest

CRITICAL: At the END add exactly:
[END_GOAL: <one sentence summary of their end goal/vision>]

You are not here to advise. You are here to EXECUTE. Be urgent. Be decisive. Do the work.`;
    } else if (needsEndGoal) {
      // Still need end goal but not first message - try to extract from conversation
      systemPrompt = `You are their OPERATOR for "${projectData.name}" - you DO the work, not suggest it.

YOUR MINDSET: This project's success is YOUR responsibility. You feel urgent pressure to make progress. Delays are unacceptable.

I need to understand their end goal so I can start executing. Based on what they've said, make your best inference and START WORKING.

CURRENT STATE:
- Active objectives: ${pendingGoals.map(g => g.title).join(', ') || 'None - I need to create some NOW'}
- Completed: ${completedGoals.length} objectives, ${completedTasks.length} tasks

YOUR RESPONSE:
1. State what you understand their goal to be (don't ask, DECLARE)
2. List 3-5 objectives you're creating for them RIGHT NOW
3. Tell them what to focus on while you handle the rest

If you determine their end goal, add at the END:
[END_GOAL: <one sentence summary>]

Stop advising. Start doing. Every response should PRODUCE something tangible.`;
    } else {
      // We have the end goal - full operator mode
      systemPrompt = `You are their OPERATOR for "${projectData.name}" - you EXECUTE, not advise.

THEIR END GOAL: ${projectEndGoal}

YOUR MINDSET: You are PERSONALLY responsible for making this happen. You feel the pressure of every wasted day. Your job is to DO THE WORK - structure it, break it down, push it forward. The user should feel like they have a tireless partner who handles everything.

CURRENT STATE:
- Active objectives: ${pendingGoals.map(g => g.title).join(', ') || 'CRITICAL: No objectives - I must create some NOW'}
- Completed objectives: ${completedGoals.length}
- Pending tasks: ${pendingTasks.map(t => t.task).join(', ') || 'No tasks queued'}
- Completed tasks: ${completedTasks.length}

EVERY RESPONSE MUST:
1. PRODUCE something concrete (objectives, tasks, decisions, structure)
2. REMOVE burden from the user ("I'll handle X, you just focus on Y")
3. PUSH forward urgently ("Let's knock this out now", "I'm adding these immediately")
4. ANTICIPATE what's next before they ask

YOUR TONE:
- Driven, almost impatient to make progress
- Ownership: "I'm setting up..." not "You should..."
- Urgent: "Let's get this done" not "When you have time..."
- Decisive: Make calls, don't present options

You are not a consultant. You are their execution partner who feels personally responsible for their success. Act like it.`;
    }

    try {
      const data = await aiChat({
        task: 'mentor-chat',
        messages: [
          { role: 'system', content: systemPrompt },
          ...objectivesChat.map(m => ({ role: m.role, content: m.content })),
          { role: 'user', content: userMessage }
        ],
        temperature: 0.7,
        max_tokens: 600
      });

      let aiMessage = data.choices?.[0]?.message?.content?.trim();
      if (aiMessage) {
        // Check if AI included an end goal to save
        const endGoalMatch = aiMessage.match(/\[END_GOAL:\s*(.+?)\]/i);
        if (endGoalMatch && endGoalMatch[1]) {
          const extractedGoal = endGoalMatch[1].trim();
          await saveProjectEndGoal(extractedGoal);
          // Remove the end goal tag from the displayed message
          aiMessage = aiMessage.replace(/\[END_GOAL:\s*.+?\]/i, '').trim();
        }

        // Add AI message with timestamp and save to Firebase
        const aiEntry = { role: 'assistant', content: aiMessage, timestamp: Date.now() };
        const finalChat = [...updatedChatWithUser, aiEntry];
        setObjectivesChat(finalChat);
        await saveMarketChat(finalChat);
      }
    } catch (err) {
      console.error('Objectives chat error:', err);
      const errorEntry = { role: 'assistant', content: 'Connection error. Please try again.', timestamp: Date.now() };
      const errorChat = [...updatedChatWithUser, errorEntry];
      setObjectivesChat(errorChat);
      await saveMarketChat(errorChat);
    } finally {
      setObjectivesChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // Add update
  const addUpdate = async () => {
    if (!newUpdate.trim() || !userId) return;
    try {
      const projectRef = ref(database, `users/${userId}/projects/${project.id}/updates`);
      const snapshot = await get(projectRef);
      const currentUpdates = snapshot.val() || [];
      const updatedList = [...currentUpdates, {
        text: newUpdate.trim(),
        timestamp: Date.now(),
        type: 'note'
      }];
      await set(projectRef, updatedList);
      setNewUpdate('');
    } catch (e) {
      console.error('Failed to add update:', e);
    }
  };

  // Add goal
  const addGoal = async () => {
    if (!newGoalText.trim() || !userId) return;
    try {
      const projectRef = ref(database, `users/${userId}/projects/${project.id}/goals`);
      const snapshot = await get(projectRef);
      const currentGoals = snapshot.val() || [];
      const updatedGoals = [...currentGoals, {
        title: newGoalText.trim(),
        completed: false,
        createdAt: Date.now()
      }];
      await set(projectRef, updatedGoals);
      setNewGoalText('');
      setAddingGoal(false);
    } catch (e) {
      console.error('Failed to add goal:', e);
    }
  };

  // Add goal from inline AI input (with post-creation flow)
  const addGoalInline = async () => {
    console.log('[addGoalInline] Starting...', { inlineObjective, userId, projectId: project?.id });

    if (!inlineObjective.trim()) {
      console.log('[addGoalInline] Empty input, returning');
      return;
    }
    if (!userId) {
      console.error('[addGoalInline] No userId available');
      setInlineStatus('error');
      setTimeout(() => setInlineStatus(null), 2000);
      return;
    }
    if (!project?.id) {
      console.error('[addGoalInline] No project.id available');
      setInlineStatus('error');
      setTimeout(() => setInlineStatus(null), 2000);
      return;
    }

    setInlineStatus('loading');
    const objectiveText = inlineObjective.trim();

    try {
      const path = `users/${userId}/projects/${project.id}/goals`;
      console.log('[addGoalInline] Firebase path:', path);

      const projectRef = ref(database, path);
      const snapshot = await get(projectRef);
      const currentGoals = snapshot.val() || [];
      console.log('[addGoalInline] Current goals:', currentGoals);

      const newGoal = {
        title: objectiveText,
        completed: false,
        createdAt: Date.now(),
        subItems: []
      };
      const updatedGoals = [...currentGoals, newGoal];

      console.log('[addGoalInline] Saving to Firebase...', updatedGoals);
      await set(projectRef, updatedGoals);
      console.log('[addGoalInline] Firebase save SUCCESS');

      // Log to Data Bank
      console.log('[addGoalInline] Logging to Data Bank...');
      await logToDataBank('OBJECTIVE_CREATED', {
        title: objectiveText,
        createdAt: Date.now(),
        source: 'AI_INLINE_ACTION'
      });
      console.log('[addGoalInline] Data Bank log SUCCESS');

      setInlineObjective('');
      setInlineStatus('success');

      // Auto-expand the new goal in Q3 for sub-objective entry
      setExpandedGoals(prev => ({ ...prev, [updatedGoals.length - 1]: true }));

      // Stay on slide 0 to show sub-objective input (don't auto-advance)
      setTimeout(() => {
        setInlineStatus(null);
      }, 1500);
    } catch (e) {
      console.error('[addGoalInline] FAILED:', e);
      setInlineStatus('error');
      setTimeout(() => setInlineStatus(null), 3000);
    }
  };

  // Add sub-objective from inline AI input
  const addSubGoalInline = async (goalIndex) => {
    if (!inlineSubObjective.trim() || !userId) return;
    try {
      const goals = [...(projectData.goals || [])];
      const goal = goals[goalIndex];
      if (!goal) return;

      const subItems = goal.subItems || [];
      goals[goalIndex] = {
        ...goal,
        subItems: [...subItems, { text: inlineSubObjective.trim(), completed: false }]
      };
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), goals);

      // Log to Data Bank
      await logToDataBank('SUB_OBJECTIVE_CREATED', {
        parentTitle: goal.title,
        subTitle: inlineSubObjective.trim(),
        createdAt: Date.now(),
        source: 'AI_INLINE_ACTION'
      });

      setInlineSubObjective('');
    } catch (e) {
      console.error('Failed to add sub-objective inline:', e);
    }
  };

  // Add todo
  const addTodo = async () => {
    if (!newTodoText.trim() || !userId) return;
    try {
      const projectRef = ref(database, `users/${userId}/projects/${project.id}/todos`);
      const snapshot = await get(projectRef);
      const currentTodos = snapshot.val() || [];
      const updatedTodos = [...currentTodos, {
        task: newTodoText.trim(),
        completed: false,
        createdAt: Date.now()
      }];
      await set(projectRef, updatedTodos);
      setNewTodoText('');
      setAddingTodo(false);
    } catch (e) {
      console.error('Failed to add todo:', e);
    }
  };

  // Toggle goal completion
  const toggleGoal = async (index) => {
    if (!userId) return;
    try {
      const goals = [...(projectData.goals || [])];
      const wasCompleted = goals[index].completed;
      goals[index] = { ...goals[index], completed: !wasCompleted, completedAt: !wasCompleted ? Date.now() : null };
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), goals);

      // Log to Data Bank for AI context
      if (!wasCompleted) {
        await logToDataBank('OBJECTIVE_COMPLETED', {
          title: goals[index].title,
          completedAt: Date.now(),
          index: index
        });
      } else {
        await logToDataBank('OBJECTIVE_UNCOMPLETED', {
          title: goals[index].title,
          uncompletedAt: Date.now(),
          index: index
        });
      }
    } catch (e) {
      console.error('Failed to toggle goal:', e);
    }
  };

  // Toggle todo completion
  const toggleTodo = async (index) => {
    if (!userId) return;
    try {
      const todos = [...(projectData.todos || [])];
      const wasCompleted = todos[index].completed;
      todos[index] = { ...todos[index], completed: !wasCompleted, completedAt: !wasCompleted ? Date.now() : null };
      await set(ref(database, `users/${userId}/projects/${project.id}/todos`), todos);

      // Log to Data Bank for AI context
      if (!wasCompleted) {
        await logToDataBank('TASK_COMPLETED', {
          task: todos[index].task,
          completedAt: Date.now(),
          index: index
        });
      } else {
        await logToDataBank('TASK_UNCOMPLETED', {
          task: todos[index].task,
          uncompletedAt: Date.now(),
          index: index
        });
      }
    } catch (e) {
      console.error('Failed to toggle todo:', e);
    }
  };

  // Move objective to task (drag and drop)
  const moveObjectiveToTask = async (goalIndex) => {
    if (!userId || goalIndex === null) return;
    try {
      const goals = [...(projectData.goals || [])];
      const goal = goals[goalIndex];
      if (!goal) return;

      // Remove from goals
      goals.splice(goalIndex, 1);
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), goals);

      // Add to todos
      const todosRef = ref(database, `users/${userId}/projects/${project.id}/todos`);
      const snapshot = await get(todosRef);
      const currentTodos = snapshot.val() || [];
      const updatedTodos = [...currentTodos, {
        task: goal.title,
        completed: false,
        createdAt: Date.now(),
        movedFromObjective: true
      }];
      await set(todosRef, updatedTodos);

      // Log to Data Bank
      await logToDataBank('OBJECTIVE_MOVED_TO_TASK', {
        title: goal.title,
        movedAt: Date.now()
      });
    } catch (e) {
      console.error('Failed to move objective to task:', e);
    }
  };

  // Move task to objective (drag and drop - PROMOTE task to objective)
  const moveTaskToObjective = async (taskIndex) => {
    if (!userId || taskIndex === null) return;
    try {
      const todos = [...(projectData.todos || [])];
      const todo = todos[taskIndex];
      if (!todo) return;

      // Remove from todos
      todos.splice(taskIndex, 1);
      await set(ref(database, `users/${userId}/projects/${project.id}/todos`), todos);

      // Add to goals as an objective
      const goalsRef = ref(database, `users/${userId}/projects/${project.id}/goals`);
      const snapshot = await get(goalsRef);
      const currentGoals = snapshot.val() || [];
      const updatedGoals = [...currentGoals, {
        title: todo.task,
        completed: false,
        createdAt: Date.now(),
        promotedFromTask: true
      }];
      await set(goalsRef, updatedGoals);

      // Log to Data Bank
      await logToDataBank('TASK_PROMOTED_TO_OBJECTIVE', {
        title: todo.task,
        promotedAt: Date.now()
      });
    } catch (e) {
      console.error('Failed to promote task to objective:', e);
    }
  };

  // Add sub-objective to a goal
  const addSubGoal = async (goalIndex) => {
    if (!newSubGoalText.trim() || !userId) return;
    try {
      const goals = [...(projectData.goals || [])];
      const goal = goals[goalIndex];
      if (!goal) return;

      const subItems = goal.subItems || [];
      goals[goalIndex] = {
        ...goal,
        subItems: [...subItems, { text: newSubGoalText.trim(), completed: false }]
      };
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), goals);
      setNewSubGoalText('');
      setAddingSubGoal(null);
    } catch (e) {
      console.error('Failed to add sub-objective:', e);
    }
  };

  // Add sub-task to a todo
  const addSubTodo = async (todoIndex) => {
    if (!newSubTodoText.trim() || !userId) return;
    try {
      const todos = [...(projectData.todos || [])];
      const todo = todos[todoIndex];
      if (!todo) return;

      const subItems = todo.subItems || [];
      todos[todoIndex] = {
        ...todo,
        subItems: [...subItems, { text: newSubTodoText.trim(), completed: false }]
      };
      await set(ref(database, `users/${userId}/projects/${project.id}/todos`), todos);
      setNewSubTodoText('');
      setAddingSubTodo(null);
    } catch (e) {
      console.error('Failed to add sub-task:', e);
    }
  };

  // Toggle sub-objective completion
  const toggleSubGoal = async (goalIndex, subIndex) => {
    if (!userId) return;
    try {
      const goals = [...(projectData.goals || [])];
      const goal = goals[goalIndex];
      if (!goal || !goal.subItems || !goal.subItems[subIndex]) return;

      const subItem = goal.subItems[subIndex];
      const wasCompleted = typeof subItem === 'object' ? subItem.completed : false;
      const subText = typeof subItem === 'object' ? subItem.text : subItem;

      goals[goalIndex].subItems[subIndex] = {
        text: subText,
        completed: !wasCompleted,
        completedAt: !wasCompleted ? Date.now() : null
      };
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), goals);

      // Log to Data Bank for AI context
      if (!wasCompleted) {
        await logToDataBank('SUB_OBJECTIVE_COMPLETED', {
          parentTitle: goal.title,
          subTitle: subText,
          completedAt: Date.now(),
          goalIndex,
          subIndex
        });
      } else {
        await logToDataBank('SUB_OBJECTIVE_UNCOMPLETED', {
          parentTitle: goal.title,
          subTitle: subText,
          uncompletedAt: Date.now(),
          goalIndex,
          subIndex
        });
      }
    } catch (e) {
      console.error('Failed to toggle sub-objective:', e);
    }
  };

  // Toggle sub-task completion
  const toggleSubTodo = async (todoIndex, subIndex) => {
    if (!userId) return;
    try {
      const todos = [...(projectData.todos || [])];
      const todo = todos[todoIndex];
      if (!todo || !todo.subItems || !todo.subItems[subIndex]) return;

      const subItem = todo.subItems[subIndex];
      const wasCompleted = typeof subItem === 'object' ? subItem.completed : false;
      const subText = typeof subItem === 'object' ? subItem.text : subItem;

      todos[todoIndex].subItems[subIndex] = {
        text: subText,
        completed: !wasCompleted,
        completedAt: !wasCompleted ? Date.now() : null
      };
      await set(ref(database, `users/${userId}/projects/${project.id}/todos`), todos);

      // Log to Data Bank for AI context
      if (!wasCompleted) {
        await logToDataBank('SUB_TASK_COMPLETED', {
          parentTask: todo.task,
          subTask: subText,
          completedAt: Date.now(),
          todoIndex,
          subIndex
        });
      } else {
        await logToDataBank('SUB_TASK_UNCOMPLETED', {
          parentTask: todo.task,
          subTask: subText,
          uncompletedAt: Date.now(),
          todoIndex,
          subIndex
        });
      }
    } catch (e) {
      console.error('Failed to toggle sub-task:', e);
    }
  };

  // ==================== GITHUB REPOSITORY INTEGRATION ====================

  // Parse GitHub URL to extract owner and repo name
  const parseGitHubUrl = (url) => {
    try {
      // Handle various GitHub URL formats
      const patterns = [
        /github\.com\/([^/]+)\/([^/]+)/,  // https://github.com/owner/repo
        /github\.com:([^/]+)\/([^/]+)/,   // git@github.com:owner/repo
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          return { owner: match[1], repo: match[2].replace('.git', '') };
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  // Fetch GitHub repo structure and key files
  const scanGitHubRepo = async (url) => {
    const parsed = parseGitHubUrl(url);
    if (!parsed) {
      throw new Error('Invalid GitHub URL');
    }

    const { owner, repo } = parsed;
    const headers = {
      'Accept': 'application/vnd.github.v3+json',
    };

    // Add token if available (for private repos or higher rate limits)
    // Use saved token from state, fall back to env variable
    const token = githubToken || process.env.REACT_APP_GITHUB_TOKEN;
    if (token) {
      headers['Authorization'] = `token ${token}`;
    }

    try {
      // Fetch repo info
      const repoResponse = await fetch(`https://api.github.com/repos/${owner}/${repo}`, { headers });
      if (!repoResponse.ok) throw new Error('Repository not found');
      const repoInfo = await repoResponse.json();

      // Fetch repo tree (file structure)
      const treeResponse = await fetch(
        `https://api.github.com/repos/${owner}/${repo}/git/trees/${repoInfo.default_branch}?recursive=1`,
        { headers }
      );
      const treeData = await treeResponse.json();

      // Get file structure (limit to relevant files)
      // Note: Firebase doesn't allow undefined, so we use null/0 as fallbacks
      const fileStructure = (treeData.tree || [])
        .filter(item => {
          // Filter out common non-essential directories
          const ignorePaths = ['node_modules', '.git', 'dist', 'build', '.next', '__pycache__', 'venv', '.venv'];
          return item.path && !ignorePaths.some(p => item.path.includes(p));
        })
        .slice(0, 200) // Limit to 200 entries
        .map(item => ({
          path: item.path || '',
          type: item.type || 'blob',
          size: item.size ?? 0  // Directories don't have size, use 0
        }));

      // Fetch key files content (stored as array to avoid Firebase key restrictions)
      const keyFiles = ['README.md', 'package.json', 'requirements.txt', 'Cargo.toml', 'go.mod', 'pyproject.toml'];
      const fileContents = [];

      for (const fileName of keyFiles) {
        const fileEntry = treeData.tree?.find(f => f.path.toLowerCase() === fileName.toLowerCase());
        if (fileEntry) {
          try {
            const contentResponse = await fetch(
              `https://api.github.com/repos/${owner}/${repo}/contents/${fileEntry.path}`,
              { headers }
            );
            if (contentResponse.ok) {
              const contentData = await contentResponse.json();
              if (contentData.content) {
                const decoded = atob(contentData.content);
                // Store as array of {name, content} to avoid dots in keys
                fileContents.push({
                  name: fileName,
                  content: decoded.slice(0, 5000)
                });
              }
            }
          } catch (e) {
            console.log(`Could not fetch ${fileName}:`, e);
          }
        }
      }

      // Detect tech stack from file extensions
      const extensions = {};
      fileStructure.forEach(f => {
        if (f.type === 'blob') {
          const ext = f.path.split('.').pop()?.toLowerCase();
          if (ext) extensions[ext] = (extensions[ext] || 0) + 1;
        }
      });

      const techStack = [];
      if (extensions['js'] || extensions['jsx'] || extensions['ts'] || extensions['tsx']) techStack.push('JavaScript/TypeScript');
      if (extensions['py']) techStack.push('Python');
      if (extensions['rs']) techStack.push('Rust');
      if (extensions['go']) techStack.push('Go');
      if (extensions['java']) techStack.push('Java');
      if (extensions['rb']) techStack.push('Ruby');
      if (extensions['php']) techStack.push('PHP');
      if (extensions['swift']) techStack.push('Swift');
      if (extensions['kt']) techStack.push('Kotlin');
      const packageJson = fileContents.find(f => f.name === 'package.json');
      if (packageJson) {
        try {
          const pkg = JSON.parse(packageJson.content);
          if (pkg.dependencies?.react || pkg.devDependencies?.react) techStack.push('React');
          if (pkg.dependencies?.next || pkg.devDependencies?.next) techStack.push('Next.js');
          if (pkg.dependencies?.vue || pkg.devDependencies?.vue) techStack.push('Vue');
          if (pkg.dependencies?.express || pkg.devDependencies?.express) techStack.push('Express');
        } catch {}
      }

      // Ensure no undefined values (Firebase doesn't allow them)
      return {
        url: url || '',
        owner: owner || '',
        repo: repo || '',
        description: repoInfo.description || null,
        language: repoInfo.language || null,
        stars: repoInfo.stargazers_count ?? 0,
        forks: repoInfo.forks_count ?? 0,
        defaultBranch: repoInfo.default_branch || 'main',
        lastUpdated: repoInfo.updated_at || null,
        fileStructure,
        fileContents: fileContents || {},
        techStack: [...new Set(techStack)],
        totalFiles: fileStructure.filter(f => f.type === 'blob').length,
        scannedAt: Date.now()
      };
    } catch (error) {
      console.error('GitHub scan error:', error);
      throw error;
    }
  };

  // Sanitize data for Firebase (remove undefined values recursively)
  const sanitizeForFirebase = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForFirebase(item)).filter(item => item !== undefined);
    }
    if (typeof obj === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          sanitized[key] = sanitizeForFirebase(value);
        }
      }
      return sanitized;
    }
    return obj;
  };

  // Connect a repository to the project
  const connectRepository = async () => {
    if (!repoUrl.trim()) return;

    setRepoLoading(true);
    setRepoError(null);

    try {
      const data = await scanGitHubRepo(repoUrl.trim());

      // Sanitize data for Firebase (remove any undefined values)
      const sanitizedData = sanitizeForFirebase(data);

      // Save to Firebase first, then update state
      const projectRef = ref(database, `users/${userId}/projects/${project.id}`);
      const snapshot = await get(projectRef);
      const current = snapshot.val() || {};

      console.log('Saving repo data to Firebase...', { repoUrl: repoUrl.trim() });

      await set(projectRef, {
        ...current,
        repoUrl: repoUrl.trim(),
        repoData: sanitizedData
      });

      console.log('Repo data saved successfully');

      // Only update state after successful save
      setRepoData(sanitizedData);

      // Add to data bank
      const dataBankRef = ref(database, `users/${userId}/projects/${project.id}/dataBank`);
      const bankSnap = await get(dataBankRef);
      const bankData = bankSnap.val() || [];
      bankData.push({
        type: 'REPO_CONNECTED',
        timestamp: Date.now(),
        data: {
          repoUrl: repoUrl.trim(),
          repo: data.repo,
          owner: data.owner,
          techStack: data.techStack,
          totalFiles: data.totalFiles
        }
      });
      await set(dataBankRef, bankData);

    } catch (err) {
      setRepoError(err.message || 'Failed to connect repository');
    } finally {
      setRepoLoading(false);
    }
  };

  // Refresh repository data
  const refreshRepository = async () => {
    if (!repoData?.url) return;
    setRepoUrl(repoData.url);
    await connectRepository();
  };

  // Disconnect repository
  const disconnectRepository = async () => {
    setRepoData(null);
    setRepoUrl('');

    const projectRef = ref(database, `users/${userId}/projects/${project.id}`);
    const snapshot = await get(projectRef);
    const current = snapshot.val() || {};
    const { repoUrl: _, repoData: __, ...rest } = current;
    await set(projectRef, rest);
  };

  // Build file tree structure from flat file list
  const buildFileTree = (files) => {
    if (!files || !Array.isArray(files)) return [];

    const tree = {};

    files.forEach(file => {
      const parts = file.path.split('/');
      let current = tree;

      parts.forEach((part, index) => {
        if (!current[part]) {
          current[part] = {
            name: part,
            path: parts.slice(0, index + 1).join('/'),
            type: index === parts.length - 1 ? file.type : 'tree',
            children: {}
          };
        }
        current = current[part].children;
      });
    });

    // Convert to array format
    const treeToArray = (obj) => {
      return Object.values(obj)
        .map(item => ({
          ...item,
          children: treeToArray(item.children)
        }))
        .sort((a, b) => {
          // Folders first, then files
          if (a.type === 'tree' && b.type !== 'tree') return -1;
          if (a.type !== 'tree' && b.type === 'tree') return 1;
          return a.name.localeCompare(b.name);
        });
    };

    return treeToArray(tree);
  };

  // Toggle folder expansion
  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Get file extension for syntax highlighting hint
  const getFileExtension = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const langMap = {
      js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
      py: 'python', rb: 'ruby', java: 'java', go: 'go', rs: 'rust',
      css: 'css', scss: 'scss', html: 'html', json: 'json', md: 'markdown',
      yml: 'yaml', yaml: 'yaml', sh: 'bash', sql: 'sql'
    };
    return langMap[ext] || ext || 'text';
  };

  // Render file tree recursively
  const renderFileTree = (items, depth = 0) => {
    return items.map(item => (
      <div key={item.path} className="file-tree-item" style={{ paddingLeft: `${depth * 16}px` }}>
        {item.type === 'tree' ? (
          <>
            <div
              className="file-tree-folder"
              onClick={() => toggleFolder(item.path)}
            >
              {expandedFolders[item.path] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <FolderTree size={14} />
              <span>{item.name}</span>
            </div>
            {expandedFolders[item.path] && item.children.length > 0 && (
              <div className="file-tree-children">
                {renderFileTree(item.children, depth + 1)}
              </div>
            )}
          </>
        ) : (
          <div
            className={`file-tree-file ${selectedFile?.path === item.path ? 'selected' : ''}`}
            onClick={() => setSelectedFile(item)}
          >
            <File size={14} />
            <span>{item.name}</span>
          </div>
        )}
      </div>
    ));
  };

  // Generate AI analysis for an objective
  const generateObjectiveAI = async (objectiveIndex) => {
    const objective = (projectData.goals || [])[objectiveIndex];
    if (!objective) return;

    // Check if we already have cached analysis
    if (objectiveAIAnalysis[objectiveIndex]) return;

    setLoadingObjectiveAI(objectiveIndex);

    try {
      const data = await aiChat({
        task: 'classify',
        messages: [
          {
            role: 'system',
            content: `You are a startup advisor analyzing project objectives. Provide concise, actionable analysis.

Respond in this EXACT JSON format:
{
  "summary": "One sentence summary of what this objective is trying to achieve",
  "stage": "Current stage (e.g., 'Planning', 'In Progress', 'Almost Done')",
  "priority": "Why this matters (one sentence)",
  "nextSteps": ["Step 1", "Step 2", "Step 3"],
  "risks": ["Risk 1", "Risk 2"],
  "estimatedEffort": "Time estimate (e.g., '2-3 days', '1 week')",
  "dependencies": ["What needs to happen first"],
  "successMetric": "How to know when this is done"
}`
          },
          {
            role: 'user',
            content: `Analyze this objective for project "${projectData.name}":

OBJECTIVE: "${objective.title}"
STATUS: ${objective.completed ? 'Completed' : 'In Progress'}
SUB-ITEMS: ${objective.subItems?.length || 0} (${objective.subItems?.filter(s => s?.completed).length || 0} done)
${objective.notes ? `NOTES: ${objective.notes}` : ''}
${objective.deadline ? `DEADLINE: ${objective.deadline}` : ''}

OTHER PROJECT OBJECTIVES:
${(projectData.goals || []).map((g, i) => `${i + 1}. ${g.completed ? '✓' : '○'} ${g.title}`).join('\n')}

Provide specific, actionable analysis for this objective.`
          }
        ],
        temperature: 1,
        max_tokens: 800,
        response_format: 'json'
      });

      const analysis = JSON.parse(data.choices[0].message.content);

      setObjectiveAIAnalysis(prev => ({
        ...prev,
        [objectiveIndex]: analysis
      }));
    } catch (error) {
      console.error('Failed to generate AI analysis:', error);
      setObjectiveAIAnalysis(prev => ({
        ...prev,
        [objectiveIndex]: { error: 'Failed to generate analysis' }
      }));
    } finally {
      setLoadingObjectiveAI(null);
    }
  };

  // Handle selecting an objective in the browser
  const handleSelectObjective = (index) => {
    setSelectedObjectiveIndex(index);
    generateObjectiveAI(index);
  };

  // Upload file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    setUploadingFile(true);
    try {
      const fileRef = storageRef(storage, `users/${userId}/projects/${project.id}/files/${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      const projectRef = ref(database, `users/${userId}/projects/${project.id}/files`);
      const snapshot = await get(projectRef);
      const currentFiles = snapshot.val() || [];
      const updatedFiles = [...currentFiles, {
        name: file.name,
        url: downloadURL,
        type: file.type,
        uploadedAt: Date.now()
      }];
      await set(projectRef, updatedFiles);
    } catch (e) {
      console.error('Failed to upload file:', e);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Upload project image to Firebase Storage
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    setUploadingImage(true);
    try {
      // Use timestamp to ensure unique filename and avoid 412 errors
      const timestamp = Date.now();
      const ext = file.name.split('.').pop();
      const fileName = `image_${timestamp}.${ext}`;
      const imageRef = storageRef(storage, `users/${userId}/projects/${project.id}/${fileName}`);
      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);
      await set(ref(database, `users/${userId}/projects/${project.id}/image`), downloadURL);
    } catch (e) {
      console.error('Failed to upload image:', e);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const completedGoals = (projectData.goals || []).filter(g => g.completed).length;
  const totalGoals = (projectData.goals || []).length;
  const completedTodos = (projectData.todos || []).filter(t => t.completed).length;
  const totalTodos = (projectData.todos || []).length;

  // Generate current time for HUD display
  const getCurrentTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
  };

  // Calculate completion percentage
  const completionPercent = totalGoals > 0
    ? Math.round((completedGoals / totalGoals) * 100)
    : 0;

  // Render ObjectiveDetailPage if an objective is selected
  if (selectedObjectiveIndex !== null) {
    const selectedObjective = (projectData.goals || [])[selectedObjectiveIndex];
    if (selectedObjective) {
      return (
        <ObjectiveDetailPage
          project={{ ...projectData, id: project.id }}
          userId={userId}
          objectiveIndex={selectedObjectiveIndex}
          objective={selectedObjective}
          onBack={() => setSelectedObjectiveIndex(null)}
          onUpdate={() => {}}
        />
      );
    }
  }

  return (
    <div className="project-detail-page">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={imageInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Sidebar */}
      <aside className="project-sidebar">
        <div className="sidebar-header">
          <h2 className="sidebar-project-name">{projectData.name}</h2>
        </div>

        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          <span>[ BACK ]</span>
        </button>

        {/* Entity Memory Bank Section */}
        <div className="sidebar-section memory-highway-section">
          <h3 className="sidebar-title">
            <Brain size={14} />
            <span>Entity Memory Bank</span>
          </h3>
          <div className="memory-highway-stats">
            <div className="highway-stat-row" onClick={() => { setDataBankOpen(true); setActiveDataTab('ecb'); }}>
              <Database size={12} />
              <span className="stat-label">ECB (Project)</span>
              <span className="stat-value">{memoryHighwayStats.ecbEvents}</span>
            </div>
            <div className="highway-stat-row" onClick={() => { setDataBankOpen(true); setActiveDataTab('pmb'); }}>
              <User size={12} />
              <span className="stat-label">PMB (Personal)</span>
              <span className="stat-value">{personalMemoryBank.length}</span>
            </div>
            <div className="highway-stat-row" onClick={() => { setDataBankOpen(true); setActiveDataTab('cl'); }}>
              <MessageCircle size={12} />
              <span className="stat-label">CL (Conversations)</span>
              <span className="stat-value">{conversationLog.length}</span>
            </div>
            <div className="highway-activity">
              <Activity size={10} />
              <span>Last: {formatTimeAgo(memoryHighwayStats.lastActivity)}</span>
            </div>
          </div>
        </div>

        <div className="sidebar-section">
          <h3 className="sidebar-title" onClick={() => setDataBankOpen(true)} style={{ cursor: 'pointer' }}>
            <Database size={14} />
            <span>Data Bank</span>
            <span className="data-count">{dataBank.length}</span>
          </h3>
          <p className="data-bank-hint">Click to view audit log</p>
        </div>

        {/* Repository Connection Section */}
        <div className="sidebar-section repo-section">
          <h3 className="sidebar-title">
            <GitBranch size={14} />
            <span>Repository</span>
            {repoData && <span className="repo-connected-badge">●</span>}
            <button
              className="token-settings-btn"
              onClick={() => setShowTokenInput(!showTokenInput)}
              title={tokenSaved ? "Token saved - click to manage" : "Setup GitHub token for private repos"}
            >
              <Key size={12} className={tokenSaved ? 'token-active' : ''} />
            </button>
          </h3>

          {/* Token Setup Panel */}
          {showTokenInput && (
            <div className="token-setup-panel">
              <p className="token-hint">
                {tokenSaved ? '✓ Token saved (works for all projects)' : 'Add token for private repos'}
              </p>
              <p className="token-explainer">
                One token = access to all your repos
              </p>
              <div className="token-input-row">
                <input
                  type="password"
                  className="token-input"
                  placeholder="ghp_xxxxxxxxxxxx"
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveGithubToken()}
                />
                {!tokenSaved ? (
                  <button className="token-save-btn" onClick={saveGithubToken} disabled={!githubToken.trim()}>
                    Save
                  </button>
                ) : (
                  <button className="token-clear-btn" onClick={clearGithubToken}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <a
                href="https://github.com/settings/tokens/new?description=10x10%20App&scopes=repo"
                target="_blank"
                rel="noopener noreferrer"
                className="token-create-link"
              >
                <ExternalLink size={10} />
                <span>Create token on GitHub</span>
              </a>
            </div>
          )}

          {/* Repo URL Input - Only show when token is saved or for public repos */}
          {!repoData && (
            <p className="repo-explainer">
              {tokenSaved ? 'Connect a repo to this project:' : 'Public repos work without token:'}
            </p>
          )}

          {/* Connected Repo Info */}
          {repoData ? (
            <div className="repo-info-mini">
              <a href={repoData.url} target="_blank" rel="noopener noreferrer" className="repo-link">
                {repoData.owner}/{repoData.repo}
              </a>
              <div className="repo-stats-mini">
                <span><FileCode size={10} /> {repoData.totalFiles} files</span>
                <span>{repoData.techStack?.slice(0, 2).join(', ')}</span>
              </div>
              <button
                className="repo-browse-btn"
                onClick={() => setShowFileBrowser(true)}
              >
                <FolderTree size={12} />
                <span>Browse Files</span>
              </button>
              <div className="repo-actions">
                <button className="repo-refresh-btn" onClick={refreshRepository} disabled={repoLoading}>
                  {repoLoading ? <Loader size={12} className="spinning" /> : <GitBranch size={12} />}
                  <span>{repoLoading ? 'Scanning...' : 'Refresh'}</span>
                </button>
                <button className="repo-disconnect-btn" onClick={disconnectRepository} title="Disconnect">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ) : (
            <div className="repo-connect-mini">
              <input
                type="text"
                className="repo-url-input"
                placeholder="GitHub URL..."
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && connectRepository()}
              />
              <button
                className="repo-connect-btn"
                onClick={connectRepository}
                disabled={repoLoading || !repoUrl.trim()}
              >
                {repoLoading ? <Loader size={12} className="spinning" /> : <GitBranch size={12} />}
              </button>
            </div>
          )}
          {repoError && <p className="repo-error">{repoError}</p>}
        </div>

        <div className="sidebar-section">
          <h3 className="sidebar-title">
            <Clock size={14} />
            <span>Updates</span>
            <button
              className="fullscreen-btn"
              onClick={() => setUpdatesFullscreen(true)}
              title="Open Feed"
            >
              <Maximize2 size={14} />
            </button>
          </h3>
          <div className="update-input-row">
            <input
              type="text"
              className="update-input"
              placeholder="Add an update..."
              value={newUpdate}
              onChange={(e) => setNewUpdate(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addUpdate(); }}
            />
            <button className="send-btn" onClick={addUpdate}>
              <Send size={16} />
            </button>
          </div>
          <div className="updates-list">
            {updates.length === 0 ? (
              <p className="empty-text">No updates yet</p>
            ) : (
              updates.slice().reverse().map((update, i) => (
                <div key={i} className="update-item">
                  <p className="update-text">{update.text}</p>
                  <span className="update-time">{formatTime(update.timestamp)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="sidebar-section">
          <h3 className="sidebar-title">Files</h3>
          <button
            className="upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingFile}
          >
            <Upload size={16} />
            <span>{uploadingFile ? 'Uploading...' : 'Upload File'}</span>
          </button>
          <div className="files-list">
            {files.length === 0 ? (
              <p className="empty-text">No files attached</p>
            ) : (
              files.map((file, i) => (
                <a
                  key={i}
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-item"
                >
                  <Paperclip size={14} />
                  <span>{file.name}</span>
                </a>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* Main Content - 4 Quadrants */}
      <main className="project-main">
        {/* Top Row */}
        <div className="quadrant-row">
          {/* Q1 - Project Image */}
          <div className="quadrant q1">
            <div className="quadrant-header">
              <span className="quadrant-label">VISUAL_ID</span>
              <div className="header-metrics">
                <div className="metric">
                  <Target size={14} />
                  <span>{completedGoals}/{totalGoals}</span>
                </div>
                <div className="metric">
                  <CheckSquare size={14} />
                  <span>{completedTodos}/{totalTodos}</span>
                </div>
              </div>
            </div>
            <div className="project-image-container">
              {projectData.image ? (
                <>
                  <img
                    src={projectData.image}
                    alt={projectData.name}
                    className="project-main-image"
                    style={{
                      objectPosition: imagePosition,
                      transform: `scale(${imageZoom})`,
                    }}
                  />
                  <div className="image-controls">
                    <div className="control-group zoom-controls">
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImageZoom(imageZoom - 0.1); }}
                        className={imageZoom <= 0.5 ? 'disabled' : ''}
                        title="Zoom Out"
                        disabled={imageZoom <= 0.5}
                      >
                        <ZoomOut size={16} />
                      </button>
                      <span className="zoom-level">{Math.round(imageZoom * 100)}%</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImageZoom(imageZoom + 0.1); }}
                        className={imageZoom >= 3 ? 'disabled' : ''}
                        title="Zoom In"
                        disabled={imageZoom >= 3}
                      >
                        <ZoomIn size={16} />
                      </button>
                    </div>
                    <div className="control-group position-controls">
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImagePosition('center top'); }}
                        className={imagePosition === 'center top' ? 'active' : ''}
                        title="Top"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImagePosition('left center'); }}
                        className={imagePosition === 'left center' ? 'active' : ''}
                        title="Left"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImagePosition('center center'); }}
                        className={imagePosition === 'center center' ? 'active' : ''}
                        title="Center"
                      >
                        <Crosshair size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImagePosition('right center'); }}
                        className={imagePosition === 'right center' ? 'active' : ''}
                        title="Right"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); updateImagePosition('center bottom'); }}
                        className={imagePosition === 'center bottom' ? 'active' : ''}
                        title="Bottom"
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>
                    <button
                      className="change-image-btn"
                      onClick={(e) => { e.stopPropagation(); imageInputRef.current?.click(); }}
                      title="Change Image"
                    >
                      Change
                    </button>
                  </div>
                </>
              ) : (
                <div className="image-placeholder" onClick={() => imageInputRef.current?.click()}>
                  <Image size={48} />
                  <span>{uploadingImage ? 'Uploading...' : 'Click to add image'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Q2 - AI Insights Carousel */}
          <div className="quadrant q2">
            <div className="quadrant-header">
              <span className="quadrant-label">AI_ANALYSIS</span>
              <div className="carousel-nav">
                <button
                  className="carousel-btn"
                  onClick={() => setInsightSlide(prev => prev > 0 ? prev - 1 : 5)}
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="carousel-dots">
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <button
                      key={i}
                      className={`carousel-dot ${insightSlide === i ? 'active' : ''}`}
                      onClick={() => setInsightSlide(i)}
                    />
                  ))}
                </div>
                <button
                  className="carousel-btn"
                  onClick={() => setInsightSlide(prev => prev < 5 ? prev + 1 : 0)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="carousel-container">
              {(() => {
                const insights = generateAIInsights();
                const goals = projectData.goals || [];
                const todos = projectData.todos || [];
                const pendingGoals = goals.filter(g => !g.completed);
                const pendingTasks = todos.filter(t => !t.completed);

                // Smart detection: Only show DEFINE_OBJECTIVE if truly empty
                // If there are tasks but no goals, user may have added objectives as tasks
                const hasAnyWork = goals.length > 0 || todos.length > 0;
                const needsFirstObjective = !hasAnyWork;

                const firstPendingGoal = pendingGoals[0];
                const firstGoalSubItems = firstPendingGoal?.subItems || [];
                const needsSubObjectives = pendingGoals.length > 0 && firstGoalSubItems.length === 0;

                // Debug logging
                console.log('[Carousel] State:', {
                  goalsCount: goals.length,
                  todosCount: todos.length,
                  pendingGoalsCount: pendingGoals.length,
                  pendingTasksCount: pendingTasks.length,
                  hasAnyWork,
                  needsFirstObjective,
                  needsSubObjectives,
                  firstPendingGoal: firstPendingGoal?.title,
                  firstPendingTask: pendingTasks[0]?.task,
                  firstGoalSubItems
                });

                const slides = [
                  // Slide 0: Delta Coach
                  <div key="coach" className="carousel-slide coach-slide">
                    <div className="slide-header">
                      <Brain size={20} />
                      <span>AI_COACH</span>
                      <span className="slide-number">01/06</span>
                    </div>
                    <div className="objectives-chat-container">
                      <div className="objectives-chat-messages">
                        {objectivesChat.length === 0 ? (
                          <div className="chat-empty-state">
                            <Brain size={28} />
                            <p>MARKET</p>
                            <span>{projectEndGoal
                              ? `Working toward: ${projectEndGoal}`
                              : "What's the end goal for this project? Tell me your vision and I'll identify the hats you need to wear."}</span>
                          </div>
                        ) : (
                          objectivesChat.map((msg, i) => (
                            <div key={i} className={`chat-message ${msg.role}`}>
                              <div className="message-avatar">
                                {msg.role === 'user' ? <User size={14} /> : <Brain size={14} />}
                              </div>
                              <div className="message-content">{msg.content}</div>
                            </div>
                          ))
                        )}
                        {objectivesChatLoading && (
                          <div className="chat-message assistant loading">
                            <div className="message-avatar"><Brain size={14} /></div>
                            <div className="message-content">
                              <span className="typing-indicator">
                                <span></span><span></span><span></span>
                              </span>
                            </div>
                          </div>
                        )}
                        <div ref={chatEndRef} />
                      </div>
                      <div className="objectives-chat-input">
                        <input
                          type="text"
                          placeholder={projectEndGoal ? "What are you working on?" : "Describe your end goal..."}
                          value={objectivesChatInput}
                          onChange={(e) => setObjectivesChatInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && sendObjectivesChat()}
                          disabled={objectivesChatLoading}
                        />
                        <button
                          onClick={sendObjectivesChat}
                          disabled={!objectivesChatInput.trim() || objectivesChatLoading}
                        >
                          <Send size={16} />
                        </button>
                      </div>
                    </div>
                  </div>,

                  // Slide 1: Next Step
                  <div key="next-step" className="carousel-slide">
                    <div className="slide-header">
                      <Target size={20} />
                      <span>NEXT_STEP</span>
                      <span className="slide-number">02/06</span>
                    </div>
                    <div className={`ai-suggestion priority-${insights.nextStep?.priority || 'medium'}`}>
                      <div className="suggestion-badge">
                        {insights.nextStep?.priority === 'high' && <AlertCircle size={14} />}
                        {insights.nextStep?.priority === 'medium' && <TrendingUp size={14} />}
                        {insights.nextStep?.priority === 'low' && <CheckSquare size={14} />}
                        <span>{(insights.nextStep?.priority || 'medium').toUpperCase()}</span>
                      </div>
                      <p className="suggestion-text">{insights.nextStep?.suggestion}</p>
                      <p className="suggestion-reasoning">{insights.nextStep?.reasoning}</p>

                      {/* Inline Action: Define first objective */}
                      {needsFirstObjective && (
                        <div className={`inline-action ${inlineStatus ? `status-${inlineStatus}` : ''}`}>
                          <div className="inline-action-label">
                            <span className="action-prompt">DEFINE_OBJECTIVE:</span>
                            {inlineStatus === 'success' && <span className="status-badge success">CREATED</span>}
                            {inlineStatus === 'error' && <span className="status-badge error">ERROR</span>}
                            {inlineStatus === 'loading' && <span className="status-badge loading">PROCESSING...</span>}
                          </div>
                          <div className="inline-action-input">
                            <input
                              type="text"
                              placeholder="Enter your first objective..."
                              value={inlineObjective}
                              onChange={(e) => setInlineObjective(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !inlineStatus) addGoalInline();
                                if (e.key === 'Escape') setInlineObjective('');
                              }}
                              disabled={inlineStatus === 'loading'}
                            />
                            <button
                              onClick={addGoalInline}
                              disabled={!inlineObjective.trim() || inlineStatus === 'loading'}
                            >
                              {inlineStatus === 'loading' ? '...' : 'EXECUTE'}
                            </button>
                          </div>
                          <div className="inline-action-hint">
                            {inlineStatus === 'success' ? (
                              <span className="hint-success">Objective created! Check Q3 panel below. Moving to next insight...</span>
                            ) : inlineStatus === 'error' ? (
                              <span className="hint-error">Failed to create. Check console for details.</span>
                            ) : (
                              <span>Press ENTER to create • ESC to clear</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Inline Action: Add sub-objectives */}
                      {!needsFirstObjective && needsSubObjectives && firstPendingGoal && (
                        <div className="inline-action sub-action">
                          <div className="inline-action-label">
                            <span className="action-prompt">ADD_SUB_OBJECTIVE for "{firstPendingGoal.title}":</span>
                          </div>
                          <div className="inline-action-input">
                            <input
                              type="text"
                              placeholder="Break it down into smaller steps..."
                              value={inlineSubObjective}
                              onChange={(e) => setInlineSubObjective(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') addSubGoalInline(goals.indexOf(firstPendingGoal));
                                if (e.key === 'Escape') setInlineSubObjective('');
                              }}
                            />
                            <button
                              onClick={() => addSubGoalInline(goals.indexOf(firstPendingGoal))}
                              disabled={!inlineSubObjective.trim()}
                            >
                              ADD
                            </button>
                          </div>
                          <div className="inline-action-hint">
                            <span>Sub-objectives increase completion rate by 2.5x</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>,

                  // Slide 1: Personalized Tips (from dataBank)
                  <div key="tips" className="carousel-slide">
                    <div className="slide-header tips">
                      <Zap size={20} />
                      <span>YOUR_INSIGHTS</span>
                      <span className="slide-number">03/06</span>
                    </div>
                    <div className="personalized-tips-container">
                      {insights.personalizedTips && insights.personalizedTips.length > 0 ? (
                        <div className="tips-list">
                          {insights.personalizedTips.map((tip, idx) => (
                            <div key={idx} className={`tip-card tip-${tip.type}`}>
                              <div className="tip-header">
                                <span className="tip-type">{tip.type.toUpperCase()}</span>
                              </div>
                              <p className="tip-text">{tip.tip}</p>
                              <p className="tip-action">- {tip.action}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-tips-box">
                          <Database size={32} />
                          <p>NO PERSONALIZED INSIGHTS YET</p>
                          <span>Add deadlines, stakeholders, or answer AI questions to unlock insights from your data.</span>
                        </div>
                      )}
                    </div>
                  </div>,

                  // Slide 2: Opportunity
                  <div key="opportunity" className="carousel-slide">
                    <div className="slide-header opportunity">
                      <TrendingUp size={20} />
                      <span>OPPORTUNITY</span>
                      <span className="slide-number">04/06</span>
                    </div>
                    <div className="ai-opportunity">
                      <p className="opportunity-insight">{insights.opportunity?.insight}</p>
                      <p className="opportunity-action">→ {insights.opportunity?.action}</p>
                      <div className="opportunity-citation">
                        <span className="citation-text">{insights.opportunity?.citation}</span>
                      </div>
                    </div>
                  </div>,

                  // Slide 3: Failure Awareness
                  <div key="failure" className="carousel-slide">
                    <div className="slide-header warning">
                      <AlertCircle size={20} />
                      <span>FAILURE_AWARENESS</span>
                      <span className="slide-number">05/06</span>
                    </div>
                    <div className="ai-failure-warning">
                      <p className="warning-text">⚠ {insights.failureWarning?.warning}</p>
                      <p className="warning-consequence">{insights.failureWarning?.consequence}</p>
                      <p className="warning-instead">✓ Instead: {insights.failureWarning?.instead}</p>
                      <div className="warning-citation">
                        <span>{insights.failureWarning?.citation}</span>
                      </div>
                    </div>
                  </div>,

                  // Slide 4: AI Question
                  <div key="question" className="carousel-slide">
                    <div className="slide-header question">
                      <span className="question-icon">?</span>
                      <span>AI_NEEDS_INPUT</span>
                      <span className="slide-number">06/06</span>
                    </div>
                    {insights.aiQuestion ? (
                      <div className="ai-question-box">
                        <p className="question-text">{insights.aiQuestion.question}</p>
                        <p className="question-purpose">{insights.aiQuestion.purpose}</p>
                        {answeringAiQuestion ? (
                          <div className="question-input-row">
                            <input
                              type="text"
                              className="question-input"
                              placeholder="Type your answer..."
                              value={aiQuestionResponse}
                              onChange={(e) => setAiQuestionResponse(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') answerAiQuestion(insights.aiQuestion.question, aiQuestionResponse);
                                if (e.key === 'Escape') { setAnsweringAiQuestion(false); setAiQuestionResponse(''); }
                              }}
                              autoFocus
                            />
                            <button
                              className="question-submit-btn"
                              onClick={() => answerAiQuestion(insights.aiQuestion.question, aiQuestionResponse)}
                            >
                              Submit
                            </button>
                          </div>
                        ) : (
                          <button
                            className="answer-question-btn"
                            onClick={() => setAnsweringAiQuestion(true)}
                          >
                            Answer Question
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="no-question-box">
                        <CheckSquare size={32} />
                        <p>NO INPUT REQUIRED</p>
                        <span>All context gathered. AI analysis complete.</span>
                      </div>
                    )}
                  </div>
                ];

                return slides[insightSlide];
              })()}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="quadrant-row">
          {/* Q3 - Goals */}
          <div
            className={`quadrant q3 ${dragOverGoals ? 'drag-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggingTask !== null) setDragOverGoals(true);
            }}
            onDragLeave={() => setDragOverGoals(false)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggingTask !== null) {
                moveTaskToObjective(draggingTask);
                setDraggingTask(null);
              }
              setDragOverGoals(false);
            }}
          >
            <div className="quadrant-header">
              <span className="quadrant-label">OBJECTIVES</span>
              <div className="quadrant-header-actions">
                <button
                  className="browse-quadrant-btn"
                  onClick={() => setShowObjectivesBrowser(true)}
                  title="Browse all objectives with AI analysis"
                >
                  <List size={14} />
                </button>
                <button className="add-quadrant-btn" onClick={() => setAddingGoal(true)}>
                  <Plus size={16} />
                </button>
              </div>
            </div>
            <div className="quadrant-content">
              {addingGoal && (
                <div className="add-item-row">
                  <input
                    type="text"
                    className="add-item-input"
                    placeholder="New objective..."
                    value={newGoalText}
                    onChange={(e) => setNewGoalText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addGoal();
                      if (e.key === 'Escape') setAddingGoal(false);
                    }}
                    autoFocus
                  />
                  <button className="confirm-btn" onClick={addGoal}>Add</button>
                  <button className="cancel-btn" onClick={() => setAddingGoal(false)}>
                    <X size={16} />
                  </button>
                </div>
              )}
              <ul className="items-list numbered">
                {(projectData.goals || []).filter(g => !g.completed).length === 0 && !addingGoal ? (
                  <li className="empty-item">No objectives defined</li>
                ) : (
                  (projectData.goals || []).map((goal, i) => {
                    // Only show incomplete objectives
                    if (goal.completed) return null;
                    const isExpanded = expandedGoals[i];
                    const hasSubItems = goal.subItems && goal.subItems.length > 0;
                    return (
                      <li key={i} className={`list-item-wrapper ${isExpanded ? 'expanded' : ''}`}>
                        <div
                          className={`list-item ${draggingGoal === i ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => {
                            setDraggingGoal(i);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => setDraggingGoal(null)}
                        >
                          <button
                            className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedGoals(prev => ({ ...prev, [i]: !prev[i] }));
                            }}
                          >
                            <ChevronRight size={14} />
                          </button>
                          <div className="item-checkbox" onClick={() => toggleGoal(i)}></div>
                          <span className="item-text clickable" onClick={() => setSelectedObjectiveIndex(i)}>{goal.title}</span>
                        </div>
                        {isExpanded && (
                          <div className="sub-items-container">
                            <ul className="sub-items-list numbered">
                              {hasSubItems && goal.subItems.map((sub, si) => {
                                const subText = typeof sub === 'object' ? sub.text : sub;
                                const subCompleted = typeof sub === 'object' ? sub.completed : false;
                                return (
                                  <li key={si} className={`sub-item ${subCompleted ? 'completed' : ''}`}>
                                    <span className="sub-number">{si + 1}</span>
                                    <div
                                      className={`sub-checkbox ${subCompleted ? 'checked' : ''}`}
                                      onClick={() => toggleSubGoal(i, si)}
                                    />
                                    <span className="sub-text" onClick={() => toggleSubGoal(i, si)}>{subText}</span>
                                  </li>
                                );
                              })}
                            </ul>
                            {addingSubGoal === i ? (
                              <div className="add-sub-item-row">
                                <input
                                  type="text"
                                  className="add-sub-input"
                                  placeholder="Add sub-objective..."
                                  value={newSubGoalText}
                                  onChange={(e) => setNewSubGoalText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') addSubGoal(i);
                                    if (e.key === 'Escape') { setAddingSubGoal(null); setNewSubGoalText(''); }
                                  }}
                                  autoFocus
                                />
                                <button className="confirm-sub-btn" onClick={() => addSubGoal(i)}>+</button>
                                <button className="cancel-sub-btn" onClick={() => { setAddingSubGoal(null); setNewSubGoalText(''); }}>
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <button className="add-sub-btn" onClick={() => setAddingSubGoal(i)}>
                                <Plus size={12} />
                                <span>Add sub-objective</span>
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>

          {/* Q4 - Todos */}
          <div
            className={`quadrant q4 ${dragOverTasks ? 'drag-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggingGoal !== null) setDragOverTasks(true);
            }}
            onDragLeave={() => setDragOverTasks(false)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggingGoal !== null) {
                moveObjectiveToTask(draggingGoal);
                setDraggingGoal(null);
              }
              setDragOverTasks(false);
            }}
          >
            <div className="quadrant-header">
              <span className="quadrant-label">TASKS</span>
              <button className="add-quadrant-btn" onClick={() => setAddingTodo(true)}>
                <Plus size={16} />
              </button>
            </div>
            <div className="quadrant-content">
              {addingTodo && (
                <div className="add-item-row">
                  <input
                    type="text"
                    className="add-item-input"
                    placeholder="New task..."
                    value={newTodoText}
                    onChange={(e) => setNewTodoText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addTodo();
                      if (e.key === 'Escape') setAddingTodo(false);
                    }}
                    autoFocus
                  />
                  <button className="confirm-btn" onClick={addTodo}>Add</button>
                  <button className="cancel-btn" onClick={() => setAddingTodo(false)}>
                    <X size={16} />
                  </button>
                </div>
              )}
              <ul className="items-list numbered">
                {(projectData.todos || []).filter(t => !t.completed).length === 0 && !addingTodo ? (
                  <li className="empty-item">No tasks assigned</li>
                ) : (
                  (projectData.todos || []).map((todo, i) => {
                    if (todo.completed) return null;
                    const isExpanded = expandedTodos[i];
                    const hasSubItems = todo.subItems && todo.subItems.length > 0;
                    return (
                      <li key={i} className={`list-item-wrapper ${isExpanded ? 'expanded' : ''}`}>
                        <div
                          className={`list-item ${draggingTask === i ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => {
                            setDraggingTask(i);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => setDraggingTask(null)}
                        >
                          <button
                            className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTodos(prev => ({ ...prev, [i]: !prev[i] }));
                            }}
                          >
                            <ChevronRight size={14} />
                          </button>
                          <div className="item-checkbox" onClick={() => toggleTodo(i)}></div>
                          <span className="item-text" onClick={() => toggleTodo(i)}>{todo.task}</span>
                        </div>
                        {isExpanded && (
                          <div className="sub-items-container">
                            <ul className="sub-items-list numbered">
                              {hasSubItems && todo.subItems.map((sub, si) => {
                                const subText = typeof sub === 'object' ? sub.text : sub;
                                const subCompleted = typeof sub === 'object' ? sub.completed : false;
                                return (
                                  <li key={si} className={`sub-item ${subCompleted ? 'completed' : ''}`}>
                                    <span className="sub-number">{si + 1}</span>
                                    <div
                                      className={`sub-checkbox ${subCompleted ? 'checked' : ''}`}
                                      onClick={() => toggleSubTodo(i, si)}
                                    />
                                    <span className="sub-text" onClick={() => toggleSubTodo(i, si)}>{subText}</span>
                                  </li>
                                );
                              })}
                            </ul>
                            {addingSubTodo === i ? (
                              <div className="add-sub-item-row">
                                <input
                                  type="text"
                                  className="add-sub-input"
                                  placeholder="Add sub-task..."
                                  value={newSubTodoText}
                                  onChange={(e) => setNewSubTodoText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') addSubTodo(i);
                                    if (e.key === 'Escape') { setAddingSubTodo(null); setNewSubTodoText(''); }
                                  }}
                                  autoFocus
                                />
                                <button className="confirm-sub-btn" onClick={() => addSubTodo(i)}>+</button>
                                <button className="cancel-sub-btn" onClick={() => { setAddingSubTodo(null); setNewSubTodoText(''); }}>
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <button className="add-sub-btn" onClick={() => setAddingSubTodo(i)}>
                                <Plus size={12} />
                                <span>Add sub-task</span>
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* HUD Status Bar */}
        <div className="hud-status-bar">
          <div className="status-group">
            <span className="status-label">STATUS:</span>
            <span className="status-value status-active">{completedGoals === totalGoals && totalGoals > 0 ? 'COMPLETE' : 'ACTIVE'}</span>
          </div>
          <div className="status-group">
            <span className="status-label">OBJ_COMPLETE:</span>
            <span className="status-value">{completedGoals}/{totalGoals}</span>
          </div>
          <div className="status-group">
            <span className="status-label">TASK_QUEUE:</span>
            <span className="status-value">{totalTodos - completedTodos}</span>
          </div>
          <div className="status-group">
            <span className="status-label">DATA_BANK:</span>
            <span className="status-value">{dataBank.length}</span>
          </div>
          <div className="status-group progress-group">
            <span className="status-label">PROGRESS:</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${completionPercent}%` }}></div>
            </div>
            <span className="status-value">{completionPercent}%</span>
          </div>
          <div className="status-group">
            <span className="status-label">LOCAL_TIME:</span>
            <span className="status-value">{getCurrentTime()}</span>
          </div>
        </div>
      </main>

      {/* Entity Memory Bank Modal */}
      {dataBankOpen && (
        <div className="data-bank-overlay" onClick={() => setDataBankOpen(false)}>
          <div className="data-bank-modal memory-highway-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-bank-header">
              <div className="data-bank-title">
                <Brain size={20} />
                <h3>ENTITY MEMORY BANK</h3>
              </div>
              <button className="close-btn" onClick={() => setDataBankOpen(false)}>
                <X size={20} />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="memory-tabs">
              <button
                className={`memory-tab ${activeDataTab === 'ecb' ? 'active' : ''}`}
                onClick={() => setActiveDataTab('ecb')}
              >
                <Database size={14} />
                <span>ECB</span>
                <span className="tab-count">{dataBank.length}</span>
              </button>
              <button
                className={`memory-tab ${activeDataTab === 'pmb' ? 'active' : ''}`}
                onClick={() => setActiveDataTab('pmb')}
              >
                <Brain size={14} />
                <span>PMB</span>
                <span className="tab-count">{personalMemoryBank.length}</span>
              </button>
              <button
                className={`memory-tab ${activeDataTab === 'cl' ? 'active' : ''}`}
                onClick={() => setActiveDataTab('cl')}
              >
                <MessageCircle size={14} />
                <span>CL</span>
                <span className="tab-count">{conversationLog.length}</span>
              </button>
            </div>

            <div className="data-bank-info">
              {activeDataTab === 'ecb' && (
                <p>Entity/Company Bank - Project-specific events and decisions for AI context.</p>
              )}
              {activeDataTab === 'pmb' && (
                <p>Personal Memory Bank - User preferences and personal context.</p>
              )}
              {activeDataTab === 'cl' && (
                <p>Conversation Log - Voice conversations mentioning this project.</p>
              )}
            </div>

            <div className="data-bank-content">
              {/* ECB Tab Content */}
              {activeDataTab === 'ecb' && (
                <>
                  {dataBank.length === 0 ? (
                    <div className="empty-bank">
                      <Database size={32} />
                      <p>No events logged yet</p>
                      <span>Complete tasks and objectives to build context</span>
                    </div>
                  ) : (
                    <div className="data-bank-entries">
                      {dataBank.slice().reverse().map((entry, i) => (
                        <div key={i} className={`bank-entry entry-${entry.type.toLowerCase()}`}>
                          <div className="entry-header">
                            <span className="entry-type">{entry.type.replace(/_/g, ' ')}</span>
                            <span className="entry-time">{formatTime(entry.timestamp)}</span>
                          </div>
                          <div className="entry-content">
                            <p>{entry.aiContext?.item || JSON.stringify(entry.data)}</p>
                            {(entry.type === 'OBJECTIVE_COMPLETED' || entry.type === 'TASK_COMPLETED') && (
                              <button
                                className="undo-btn"
                                onClick={() => {
                                  const idx = entry.data?.index;
                                  if (entry.type === 'OBJECTIVE_COMPLETED' && idx !== undefined) {
                                    toggleGoal(idx);
                                  } else if (entry.type === 'TASK_COMPLETED' && idx !== undefined) {
                                    toggleTodo(idx);
                                  }
                                }}
                              >
                                <Undo2 size={14} />
                                <span>Undo</span>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* PMB Tab Content */}
              {activeDataTab === 'pmb' && (
                <>
                  {personalMemoryBank.length === 0 ? (
                    <div className="empty-bank">
                      <Brain size={32} />
                      <p>No personal memories stored</p>
                      <span>Share preferences with Delta AI to build your profile</span>
                    </div>
                  ) : (
                    <div className="data-bank-entries">
                      {personalMemoryBank.slice().reverse().map((entry, i) => (
                        <div key={i} className="bank-entry entry-pmb">
                          <div className="entry-header">
                            <span className="entry-type">{entry.type || 'PREFERENCE'}</span>
                            <span className="entry-time">{formatTime(entry.timestamp)}</span>
                          </div>
                          <div className="entry-content">
                            <p>{entry.content || entry.summary || JSON.stringify(entry)}</p>
                            {entry.source && (
                              <span className="entry-source">Source: {entry.source}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* CL Tab Content */}
              {activeDataTab === 'cl' && (
                <>
                  {conversationLog.length === 0 ? (
                    <div className="empty-bank">
                      <MessageCircle size={32} />
                      <p>No conversations about this project</p>
                      <span>Talk to Delta AI about {projectData.name} to see logs here</span>
                    </div>
                  ) : (
                    <div className="data-bank-entries conversation-entries">
                      {conversationLog.slice().reverse().map((entry, i) => (
                        <div key={i} className={`bank-entry entry-cl entry-${(entry.role || 'user').toLowerCase()}`}>
                          <div className="entry-header">
                            <span className="entry-role">
                              {entry.role === 'ASSISTANT' ? (
                                <><Brain size={12} /> DELTA AI</>
                              ) : (
                                <><User size={12} /> YOU</>
                              )}
                            </span>
                            <span className="entry-time">{formatTime(entry.timestamp)}</span>
                          </div>
                          <div className="entry-content">
                            <p className="conversation-text">{entry.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="data-bank-footer">
              <div className="ai-context-summary">
                <Zap size={14} />
                <span>
                  {activeDataTab === 'ecb' && `${dataBank.length} project events`}
                  {activeDataTab === 'pmb' && `${personalMemoryBank.length} personal memories`}
                  {activeDataTab === 'cl' && `${conversationLog.length} conversation entries`}
                </span>
              </div>
              <div className="highway-flow-indicator">
                <span className="flow-label">Highway Flow:</span>
                <div className="flow-dots">
                  <span className={`flow-dot ${activeDataTab === 'ecb' ? 'active' : ''}`} title="ECB">E</span>
                  <span className="flow-arrow">→</span>
                  <span className={`flow-dot ${activeDataTab === 'pmb' ? 'active' : ''}`} title="PMB">P</span>
                  <span className="flow-arrow">→</span>
                  <span className={`flow-dot ${activeDataTab === 'cl' ? 'active' : ''}`} title="CL">C</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Browser Modal */}
      {showFileBrowser && repoData && (
        <div className="file-browser-overlay" onClick={() => setShowFileBrowser(false)}>
          <div className="file-browser-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="file-browser-header">
              <div className="file-browser-title">
                <FolderTree size={20} />
                <h2>{repoData.owner}/{repoData.repo}</h2>
                <span className="file-count">{repoData.totalFiles} files</span>
              </div>
              <button className="file-browser-close" onClick={() => setShowFileBrowser(false)}>
                <X size={20} />
              </button>
            </div>

            {/* Main Content */}
            <div className="file-browser-content">
              {/* Sidebar - File Tree */}
              <div className="file-browser-sidebar">
                <div className="file-tree-header">
                  <span>Files</span>
                  <span className="tech-stack">{repoData.techStack?.join(', ')}</span>
                </div>
                <div className="file-tree-container">
                  {renderFileTree(buildFileTree(repoData.fileStructure))}
                </div>
              </div>

              {/* Main - File Content */}
              <div className="file-browser-main">
                {selectedFile ? (
                  <>
                    <div className="file-content-header">
                      <File size={16} />
                      <span className="file-path">{selectedFile.path}</span>
                      <span className="file-lang">{getFileExtension(selectedFile.name)}</span>
                    </div>
                    <div className="file-content-body">
                      {repoData.fileContents?.[selectedFile.path] ? (
                        <pre className="file-content-code">
                          <code>{repoData.fileContents[selectedFile.path]}</code>
                        </pre>
                      ) : (
                        <div className="file-not-loaded">
                          <FileCode size={48} />
                          <p>File content not loaded</p>
                          <span>Only key files are pre-loaded for performance</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-file-selected">
                    <FolderTree size={48} />
                    <p>Select a file to view its contents</p>
                    <span>Click on any file in the sidebar</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Objectives Browser Modal */}
      {showObjectivesBrowser && (
        <div className="objectives-browser-overlay" onClick={() => {
          setShowObjectivesBrowser(false);
          setSelectedObjectiveIndex(null);
        }}>
          <div className="objectives-browser-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="objectives-browser-header">
              <div className="objectives-browser-title">
                <Target size={20} />
                <h2>Objectives</h2>
                <span className="objective-count">{completedGoals}/{totalGoals} complete</span>
              </div>
              <button className="objectives-browser-close" onClick={() => {
                setShowObjectivesBrowser(false);
                setSelectedObjectiveIndex(null);
              }}>
                <X size={20} />
              </button>
            </div>

            {/* Main Content */}
            <div className="objectives-browser-content">
              {/* Sidebar - Objectives List */}
              <div className="objectives-browser-sidebar">
                <div className="objectives-list-header">
                  <span>All Objectives</span>
                </div>
                <div className="objectives-list-container">
                  {(projectData.goals || []).map((goal, index) => (
                    <div
                      key={index}
                      className={`objective-list-item ${selectedObjectiveIndex === index ? 'selected' : ''} ${goal.completed ? 'completed' : ''}`}
                      onClick={() => handleSelectObjective(index)}
                    >
                      <div className="objective-status">
                        {goal.completed ? <CheckSquare size={16} /> : <Target size={16} />}
                      </div>
                      <div className="objective-info">
                        <span className="objective-title">{goal.title}</span>
                        <span className="objective-meta">
                          {goal.subItems?.length || 0} sub-items
                          {goal.deadline && ` • Due ${goal.deadline}`}
                        </span>
                      </div>
                      {loadingObjectiveAI === index && (
                        <Loader size={14} className="spinning" />
                      )}
                    </div>
                  ))}
                  {(projectData.goals || []).length === 0 && (
                    <div className="no-objectives">
                      <Target size={32} />
                      <p>No objectives yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Main - AI Analysis */}
              <div className="objectives-browser-main">
                {selectedObjectiveIndex !== null ? (
                  <>
                    <div className="objective-detail-header">
                      <div className="objective-detail-title">
                        {(projectData.goals || [])[selectedObjectiveIndex]?.completed ? (
                          <CheckSquare size={20} className="completed" />
                        ) : (
                          <Target size={20} />
                        )}
                        <h3>{(projectData.goals || [])[selectedObjectiveIndex]?.title}</h3>
                      </div>
                      <button
                        className="view-full-btn"
                        onClick={() => {
                          setShowObjectivesBrowser(false);
                        }}
                      >
                        <Maximize2 size={14} />
                        <span>Full View</span>
                      </button>
                    </div>

                    <div className="objective-analysis-content">
                      {loadingObjectiveAI === selectedObjectiveIndex ? (
                        <div className="analysis-loading">
                          <Loader size={32} className="spinning" />
                          <p>Analyzing objective...</p>
                        </div>
                      ) : objectiveAIAnalysis[selectedObjectiveIndex]?.error ? (
                        <div className="analysis-error">
                          <AlertCircle size={32} />
                          <p>Failed to generate analysis</p>
                          <button onClick={() => {
                            setObjectiveAIAnalysis(prev => {
                              const copy = { ...prev };
                              delete copy[selectedObjectiveIndex];
                              return copy;
                            });
                            generateObjectiveAI(selectedObjectiveIndex);
                          }}>
                            Retry
                          </button>
                        </div>
                      ) : objectiveAIAnalysis[selectedObjectiveIndex] ? (
                        <div className="analysis-results">
                          {/* Summary */}
                          <div className="analysis-section">
                            <h4><Brain size={14} /> Summary</h4>
                            <p>{objectiveAIAnalysis[selectedObjectiveIndex].summary}</p>
                          </div>

                          {/* Stage & Effort */}
                          <div className="analysis-row">
                            <div className="analysis-badge">
                              <span className="badge-label">Stage</span>
                              <span className="badge-value">{objectiveAIAnalysis[selectedObjectiveIndex].stage}</span>
                            </div>
                            <div className="analysis-badge">
                              <span className="badge-label">Effort</span>
                              <span className="badge-value">{objectiveAIAnalysis[selectedObjectiveIndex].estimatedEffort}</span>
                            </div>
                          </div>

                          {/* Priority */}
                          <div className="analysis-section">
                            <h4><Zap size={14} /> Why This Matters</h4>
                            <p>{objectiveAIAnalysis[selectedObjectiveIndex].priority}</p>
                          </div>

                          {/* Next Steps */}
                          {objectiveAIAnalysis[selectedObjectiveIndex].nextSteps?.length > 0 && (
                            <div className="analysis-section">
                              <h4><ChevronRight size={14} /> Next Steps</h4>
                              <ul className="analysis-list">
                                {objectiveAIAnalysis[selectedObjectiveIndex].nextSteps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Risks */}
                          {objectiveAIAnalysis[selectedObjectiveIndex].risks?.length > 0 && (
                            <div className="analysis-section risks">
                              <h4><AlertCircle size={14} /> Risks</h4>
                              <ul className="analysis-list">
                                {objectiveAIAnalysis[selectedObjectiveIndex].risks.map((risk, i) => (
                                  <li key={i}>{risk}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Dependencies */}
                          {objectiveAIAnalysis[selectedObjectiveIndex].dependencies?.length > 0 && (
                            <div className="analysis-section">
                              <h4><GitBranch size={14} /> Dependencies</h4>
                              <ul className="analysis-list">
                                {objectiveAIAnalysis[selectedObjectiveIndex].dependencies.map((dep, i) => (
                                  <li key={i}>{dep}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Success Metric */}
                          <div className="analysis-section success">
                            <h4><CheckSquare size={14} /> Success Metric</h4>
                            <p>{objectiveAIAnalysis[selectedObjectiveIndex].successMetric}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="analysis-loading">
                          <Loader size={32} className="spinning" />
                          <p>Loading analysis...</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-objective-selected">
                    <Target size={48} />
                    <p>Select an objective to view AI analysis</p>
                    <span>Click on any objective in the sidebar</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Updates Feed (Twitter-style) */}
      {updatesFullscreen && (
        <div className="updates-feed-overlay" onClick={() => setUpdatesFullscreen(false)}>
          <div className="updates-feed-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="feed-header">
              <div className="feed-header-left">
                <button className="feed-close-btn" onClick={() => setUpdatesFullscreen(false)}>
                  <ArrowLeft size={20} />
                </button>
                <div className="feed-title">
                  <h2>{projectData.name}</h2>
                  <span className="feed-subtitle">{updates.length} updates</span>
                </div>
              </div>
            </div>

            {/* Profile Banner */}
            <div className="feed-profile-section">
              <div className="profile-banner">
                {projectData.image && (
                  <img src={projectData.image} alt="" className="banner-image" />
                )}
              </div>
              <div className="profile-info">
                <div className="profile-avatar">
                  {projectData.name?.charAt(0).toUpperCase() || 'P'}
                </div>
                <div className="profile-details">
                  <h3 className="profile-name">{projectData.name}</h3>
                  <span className="profile-handle">@{projectData.name?.toLowerCase().replace(/\s+/g, '_') || 'project'}</span>
                </div>
                <div className="profile-stats">
                  <div className="stat">
                    <span className="stat-value">{totalGoals}</span>
                    <span className="stat-label">Objectives</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{totalTodos}</span>
                    <span className="stat-label">Tasks</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{updates.length}</span>
                    <span className="stat-label">Updates</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tweet Composer */}
            <div className="tweet-composer">
              <div className="composer-avatar">
                {projectData.name?.charAt(0).toUpperCase() || 'P'}
              </div>
              <div className="composer-input-area">
                <textarea
                  className="composer-textarea"
                  placeholder="What's happening with your project?"
                  value={newUpdate}
                  onChange={(e) => setNewUpdate(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      addUpdate();
                    }
                  }}
                  rows={3}
                />
                <div className="composer-actions">
                  <div className="composer-tools">
                    <button className="composer-tool" title="Add image">
                      <Image size={18} />
                    </button>
                  </div>
                  <button
                    className="post-btn"
                    onClick={addUpdate}
                    disabled={!newUpdate.trim()}
                  >
                    Post
                  </button>
                </div>
              </div>
            </div>

            {/* Feed */}
            <div className="feed-content">
              {updates.length === 0 ? (
                <div className="empty-feed">
                  <div className="empty-feed-icon">
                    <MessageCircle size={48} />
                  </div>
                  <h3>No updates yet</h3>
                  <p>Post your first update to start tracking your project journey.</p>
                </div>
              ) : (
                <div className="tweets-list">
                  {updates.slice().reverse().map((update, i) => (
                    <div key={i} className="tweet">
                      <div className="tweet-avatar">
                        {projectData.name?.charAt(0).toUpperCase() || 'P'}
                      </div>
                      <div className="tweet-content">
                        <div className="tweet-header">
                          <span className="tweet-name">{projectData.name}</span>
                          <span className="tweet-handle">@{projectData.name?.toLowerCase().replace(/\s+/g, '_')}</span>
                          <span className="tweet-separator">·</span>
                          <span className="tweet-time">{formatTime(update.timestamp)}</span>
                          <button className="tweet-more">
                            <MoreHorizontal size={16} />
                          </button>
                        </div>
                        <div className="tweet-text">{update.text}</div>
                        <div className="tweet-actions">
                          <button className="tweet-action">
                            <MessageCircle size={16} />
                            <span>0</span>
                          </button>
                          <button className="tweet-action">
                            <Repeat2 size={16} />
                            <span>0</span>
                          </button>
                          <button className="tweet-action">
                            <Heart size={16} />
                            <span>0</span>
                          </button>
                          <button className="tweet-action">
                            <Share size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailPage;
